import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { Col, Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import { components } from "react-select";
import { useLocation, useNavigate } from "react-router-dom";
import NoIndexSEO from "../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../utils/seoConfig";
import { ReactComponent as FiSearch } from "../../assets/images/search-icon.svg";
import { ReactComponent as FiSliders } from "../../assets/images/filters-icon.svg";
import { ReactComponent as CrimeIcon } from "../../assets/images/search-crime-icon.svg";
import { ReactComponent as HospitalsIcon } from "../../assets/images/hospital-search.svg";
import { ReactComponent as PopulationIcon } from "../../assets/images/populations.svg";
import { ReactComponent as HighTempIcon } from "../../assets/images/high-temperature.svg";
import { ReactComponent as LowTempIcon } from "../../assets/images/low-temperature.svg";
// import { ReactComponent as HospitalsIcon } from "../../assets/images/hospital-icon-1.svg";
import { ReactComponent as CheckIcon } from "../../assets/images/check.svg";
// import { ReactComponent as Tax } from "../../assets/images/search-tax.svg";
import { ReactComponent as Tax } from "../../assets/images/search-tax-1.svg";
import { useDispatch, useSelector } from "react-redux";

import {
  getAllStates,
  getRoles,
  getAllCities,
} from "../../redux/slices/authSlice";
import {
  ComparisonTrack,
  sessionId,
} from "../../redux/slices/cityComparisonSlice";
// import { getFeatureAccess } from "../../utils/helpers/checkSubRestriction";
import {
  checkCitySearch,
  checkFilterCityCount,
  checkMultipleBookmarks,
  cityAutocomplete,
  filterCity,
  getBookmarks,
  searchAnalytics,
  searchBookmark,
  searchSessionEnd,
  searchSessionId,
  trackCityClick,
} from "../../redux/slices/searchSlice";
import { getViewProfile } from "../../redux/slices/profileSlice";
import { UserRound } from "lucide-react";
import {
  PROFILE_COMPLETION_FIELDS,
  getProfileCompletionPercent,
} from "../../utils/profileCompletion";
import ActionModal from "../comparison/ActionModal";
import { allowedNames, allowedRolesForFree } from "../../constants";
import ComparisonModal from "../restriction-modals/ComparisonModal";
import CityDetailLimitModal from "../restriction-modals/CityDetailLimitModal";
import RestrictionModal from "../restriction-modals/RestrictionModal";
import {
  activationTrack,
  activityEnd,
  activityStart,
} from "../../redux/slices/trackActivitySlice";
import usePageTracking from "../../hooks/usePageTracking";
import useTour from "../../components/tour/useTour";
import GuidedTour from "../../components/tour/GuidedTour";
import getSearchTourSteps from "./searchTourSteps";

import { ReactComponent as RemoveIcon } from "../../assets/images/menu-close-icon.svg";

import { ReactComponent as ChevronRightIcon } from "../../assets/images/btn-right-aero.svg";
import { BsGrid3X3Gap, BsListUl } from "react-icons/bs";
import { FiCheck, FiHeart } from "react-icons/fi";
import "./searchTopbar.scss";

const PROFILE_COMPLETION_FIELDS_BY_KEY = Object.fromEntries(
  PROFILE_COMPLETION_FIELDS.map((field) => [field.key, field]),
);

// Priority-ordered: the first of these still incomplete is the one shown,
// since it's the field most likely to affect which cities/results match.
const SEARCH_RELEVANT_PROFILE_NUDGES = [
  {
    key: "role",
    message: "Add your nursing role to see cities matched to your role.",
  },
  {
    key: "city",
    message: "Add your current city to see nearby results first.",
  },
  {
    key: "salary",
    message: "Add your preferred salary to compare against relevant cities.",
  },
];

/* Small popover under a quick-filter chip. Multi or single select.
   Calls onApply(nextSelected) on every pick so the caller can fire the API. */
const ChipChev = () => (
  <svg
    className="chip-chev"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);
const ChipCheckSvg = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// Popover is portaled to <body> with position:fixed so the chip-row's
// overflow-x: auto can't clip it and ancestor z-index can't cover it.
const POPOVER_MIN_W = 220;
const POPOVER_MAX_W = 320;
const POPOVER_GAP = 6;

function ChipDropdown({
  label,
  value,
  active,
  options,
  selected,
  isMulti,
  isClearable,
  onApply,
  onLockedTap,
  dataTour,
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const chipRef = useRef(null);
  const popoverRef = useRef(null);

  // Recompute popover position whenever it opens, on scroll, or on resize.
  useEffect(() => {
    if (!open) return undefined;
    const update = () => {
      if (!chipRef.current) return;
      const rect = chipRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const width = Math.min(
        POPOVER_MAX_W,
        Math.max(POPOVER_MIN_W, rect.width),
      );
      // Prefer aligning with the chip's left edge; if that would overflow the
      // viewport right edge, anchor to the chip's right edge instead.
      let left = rect.left;
      if (left + width > vw - 8) left = Math.max(8, rect.right - width);
      setPos({ top: rect.bottom + POPOVER_GAP, left, minWidth: width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  // Close on outside click / Escape. Pointer events on either the chip or the
  // (portaled) popover count as "inside".
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (chipRef.current && chipRef.current.contains(e.target)) return;
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isSelected = (opt) =>
    isMulti
      ? Array.isArray(selected) && selected.some((s) => s.value === opt.value)
      : selected != null && selected.value === opt.value;

  const pick = (opt) => {
    if (opt.isDisabled) {
      // Locked option → close the popover first so the upgrade modal
      // isn't visually layered behind it, then trigger the modal.
      setOpen(false);
      onLockedTap?.(opt);
      return;
    }
    if (isMulti) {
      const arr = Array.isArray(selected) ? selected : [];
      const next = isSelected(opt)
        ? arr.filter((s) => s.value !== opt.value)
        : [...arr, opt];
      onApply(next);
    } else {
      const next = isSelected(opt) ? null : opt;
      onApply(next);
      setOpen(false);
    }
  };

  const clear = () => onApply(isMulti ? [] : null);

  const hasSelection = isMulti
    ? Array.isArray(selected) && selected.length > 0
    : !!selected;

  const popover =
    open &&
    pos &&
    createPortal(
      <div
        ref={popoverRef}
        className="mmp-chip-popover"
        style={{
          position: "fixed",
          top: pos.top,
          left: pos.left,
          minWidth: pos.minWidth,
          zIndex: 9500,
        }}
        role="listbox"
        aria-multiselectable={isMulti}
      >
        {options.map((opt, i) => {
          const sel = isSelected(opt);
          return (
            <button
              key={`${String(opt.value)}-${i}`}
              type="button"
              className={`chip-option ${sel ? "is-selected" : ""} ${
                opt.isDisabled ? "is-locked" : ""
              }`}
              onClick={() => pick(opt)}
              role="option"
              aria-selected={sel}
              aria-disabled={opt.isDisabled || false}
            >
              {isMulti && (
                <span className="chip-check" aria-hidden="true">
                  {sel && <ChipCheckSvg />}
                </span>
              )}
              <span className="chip-option-label">{opt.label}</span>
              {opt.requiredPlan && !sel && (
                <span className="chip-option-badge">{opt.requiredPlan}</span>
              )}
            </button>
          );
        })}
        {isClearable && hasSelection && (
          <button type="button" className="chip-clear" onClick={clear}>
            Clear
          </button>
        )}
      </div>,
      document.body,
    );

  return (
    <div className="chip-wrap" data-tour={dataTour}>
      <button
        ref={chipRef}
        type="button"
        className={`chip ${active ? "is-active" : ""} ${open ? "is-open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="chip-label">{label}:</span>
        <span>{value}</span>
        <ChipChev />
      </button>
      {popover}
    </div>
  );
}

/* Skeleton placeholder for a result card — shown while results load. Mirrors
 * the real card's grid columns so the layout doesn't jump when data arrives. */
function SearchCardSkeleton({ viewMode }) {
  return (
    <div
      className={`result-card sk-result-card ${
        viewMode === "list" ? "col-12" : "col-md-6 col-xl-4"
      }`}
      aria-hidden="true"
    >
      <div className="contentwrap sk-card">
        <div className="sk-card-header">
          <div className="sk sk--lg" />
          <div className="sk sk--circle" />
        </div>
        <div className="sk-card-body">
          <div className="sk-row">
            <div className="sk sk--pill" />
            <div className="sk sk--pill sk--pill-sm" />
          </div>
          <div className="sk-row sk-row--cols">
            <div className="sk-stack">
              <div className="sk sk--xs" />
              <div className="sk sk--md" />
              <div className="sk sk--xs" />
            </div>
            <div className="sk-stack">
              <div className="sk sk--xs" />
              <div className="sk sk--md" />
              <div className="sk sk--xs" />
            </div>
          </div>
          <div className="sk-keep">
            <div className="sk sk--xs sk--w30" />
            <div className="sk-row sk-row--between">
              <div className="sk sk--sm" />
              <div className="sk sk--sm sk--w20" />
            </div>
            <div className="sk sk--bar" />
            <div className="sk-row sk-row--between">
              <div className="sk sk--sm" />
              <div className="sk sk--sm sk--w20" />
            </div>
            <div className="sk sk--bar" />
          </div>
          <div className="sk-row sk-row--between">
            <div className="sk sk--pill sk--pill-sm" />
            <div className="sk sk--xs sk--w30" />
          </div>
        </div>
      </div>
    </div>
  );
}

const salaryOptions = [
  { value: "40k-60k", label: "$40k - $60k" },
  { value: "60k-80k", label: "$60k - $80k" },
  { value: "80k-100k", label: "$80k - $100k" },
  { value: "100k-120k", label: "$100k - $120k" },
  { value: "120k-140k", label: "$120k - $140k" },
  { value: "140k-200k", label: "$140k - $200k" },
  { value: "200k+", label: "$200k+" },
];

const crimeRateOptions = [
  { value: "low", label: "Low (rate is between 0 and 33)" },
  { value: "moderate", label: "Moderate (rate is between 34 and 66)" },
  { value: "high", label: "High (rate is between 67 and 100)" },
];

// const homePriceOptions = [
//   { value: "50k-100k", label: "50k - 100k " },
//   { value: "100k-250k", label: "$100k - $250k" },
//   { value: "250k-500k", label: "$250k - $500k" },
//   { value: "500k-750k", label: "$500k - $750k" },
//   { value: "750k-1m", label: "$750k - $1M" },
//   { value: "1m-2m", label: "$1M - $2M" },
//   { value: "2m+", label: "2M+" },
// ];

const homePriceOptions = [
  { value: "under-400k", label: "Under $400K" },
  { value: "400k-600k", label: "$400K - $600K" },
  { value: "600k-1m", label: "$600K - $1M" },
  { value: "over-1m", label: "Over $1M" },
];

const mortgageOptions = [
  { value: "500-1000", label: "$500 - $1,000" },
  { value: "1000-2000", label: "$1,000 - $2,000" },
  { value: "2000-3000", label: "$2,000 - $3,000" },
  { value: "3000-4000", label: "$3,000 - $4,000" },
  { value: "4000-5000", label: "$4,000 - $5,000" },
  { value: "5000+", label: "$5,000+" },
];

// const rentOptions = [
//   { value: "500-1000", label: "$500 - $1,000" },
//   { value: "1000-2000", label: "$1,000 - $2,000" },
//   { value: "2000-3000", label: "$2,000 - $3,000" },
//   { value: "3000-4000", label: "$3,000 - $4,000" },
//   { value: "4000-5000", label: "$4,000 - $5,000" },
//   { value: "4000+", label: "$5,000+" },
// ];
const rentOptions = [
  { value: "under-1500", label: "Under $1,500" },
  { value: "1500-2500", label: "$1,500 - $2,500" },
  { value: "2500-4000", label: "$2,500 - $4,000" },
  { value: "over-4000", label: "Over $4,000" },
];
const saftyOptions = [
  { value: "any", label: "Any" },
  { value: "low_only", label: "Low Only" },
  { value: "no_severe", label: "No Severe" },
];
const compactOptions = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const hospitalCountOptions = [
  { value: "1", label: "1+ hospitals" },
  { value: "3", label: "3+ hospitals" },
  { value: "5", label: "5+ hospitals" },
  { value: "10", label: "10+ hospitals" },
];

// Climate buckets keyed on July high temperature (°F). Matches backend climateMap.
const climateOptions = [
  { value: "cool", label: "Cool summers (< 75°F)" },
  { value: "mild", label: "Mild summers (75–85°F)" },
  { value: "warm", label: "Warm summers (85–95°F)" },
  { value: "hot", label: "Hot summers (95°F+)" },
];

// const sortbyData = [
//   {
//     text: "Salary (High to Low)",
//     value: "salary-high-low",
//     sort: "desc",
//     field: "median_annual_salary",
//     track: "salary_high_to_low",
//   },
//   {
//     text: "Salary (Low to High)",
//     value: "salary-low-high",
//     sort: "asc",
//     field: "median_annual_salary",
//     track: "salary_low_to_high",
//   },
//   {
//     text: "Housing (High to Low)",
//     value: "housing-high-low",
//     sort: "desc",
//     field: "monthly_mortgage_payment_7_065_apr",
//     track: "housing_high_to_low",
//   },
//   {
//     text: "Housing (Low to High)",
//     value: "housing-low-high",
//     sort: "asc",
//     field: "monthly_mortgage_payment_7_065_apr",
//     track: "housing_low_to_high",
//   },
//   {
//     text: "Left over if owning (High to Low)",
//     value: "owning-high-low",
//     sort: "desc",
//     field: "left_over_annually_if_owning",
//     track: "owning_high_to_low",
//   },
//   {
//     text: "Left over if owning (Low to High)",
//     value: "owning-low-high",
//     sort: "asc",
//     field: "left_over_annually_if_owning",
//     track: "owning_low_to_high",
//   },
//   {
//     text: "Left over if renting (High to Low)",
//     value: "renting-high-low",
//     sort: "desc",
//     field: "left_over_annually_if_renting",
//     track: "renting_high_to_low",
//   },
//   {
//     text: "Left over if renting (Low to High)",
//     value: "renting-low-high",
//     sort: "asc",
//     field: "left_over_annually_if_renting",
//     track: "renting_low_to_high",
//   },
// ];
const sortbyData = [
  {
    text: "Salary (High to Low)",
    value: "median_annual_salary-desc",
    sort: "desc",
    field: "median_annual_salary",
    track: "salary_high_to_low",
  },
  {
    text: "Salary (Low to High)",
    value: "median_annual_salary-asc",
    sort: "asc",
    field: "median_annual_salary",
    track: "salary_low_to_high",
  },
  {
    text: "Take-Home Pay (High → Low)",
    value: "net_monthly_salary-desc",
    sort: "desc",
    field: "net_monthly_salary",
    track: "salary_high_to_low",
  },
  {
    text: "Take-Home Pay (Low → High)",
    value: "net_monthly_salary-asc",
    sort: "asc",
    field: "net_monthly_salary",
    track: "salary_low_to_high",
  },
  {
    text: "Highest Left After Mortgage",
    value: "after_mortgage-desc",
    sort: "desc",
    field: "after_mortgage",
    track: "housing_high_to_low",
  },
  {
    text: "Lowest Left After Mortgage",
    value: "after_mortgage-asc",
    sort: "asc",
    field: "after_mortgage",
    track: "housing_low_to_high",
  },
  {
    text: "Highest Left After Rent",
    value: "after_rent-desc",
    sort: "desc",
    field: "after_rent",
    track: "left_over_after_rent",
  },
  {
    text: "Lowest Left After Rent",
    value: "after_rent-asc",
    sort: "asc",
    field: "after_rent",
    track: "left_over_after_rent",
  },
  {
    text: "Highest Home Price",
    value: "home_prices-desc",
    sort: "desc",
    field: "home_prices",
    track: "housing_high_to_low",
  },
  {
    text: "Lowest Home Price",
    value: "home_prices-asc",
    sort: "asc",
    field: "home_prices",
    track: "housing_low_to_high",
  },
  {
    text: "Most Hospitals",
    value: "hospital_count-desc",
    sort: "desc",
    field: "hospital_count",
    track: "most_hospital",
  },
  {
    text: "Most Populated",
    value: "total_population-desc",
    sort: "desc",
    field: "total_population",
    track: "most_populated",
  },
  // {
  //   text: "Highest Mortgage",
  //   value: "monthly_mortgage_payment_7_065_apr",
  //   sort: "desc",
  //   field: "monthly_mortgage_payment_7_065_apr",
  //   track: "left_over_after_rent",
  // },
  // {
  //   text: "Lowest Mortgage",
  //   value: "monthly_mortgage_payment_7_065_apr",
  //   sort: "asc",
  //   field: "monthly_mortgage_payment_7_065_apr",
  //   track: "left_over_after_rent",
  // },
];
const Search = () => {
  const dispatch = useDispatch();

  // Guided tour — auto-starts on first visit, replayable from the header.
  const searchTourSteps = useMemo(() => getSearchTourSteps(), []);
  const { isOpen: isTourOpen, finish: finishTour } = useTour("search");
  const navigate = useNavigate();

  const store = useSelector((state) => state.auth);
  const [showFilters, setShowFilters] = useState(false);
  const [query, setQuery] = useState("");
  // The search term actually sent to the API. For Free users this is updated
  // ONLY after an allowed (gated) suggestion-click search — typing alone never
  // reaches the API for them. For Plus/Pro it tracks `query` (see apiSearchValue).
  const [appliedSearch, setAppliedSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const searchInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [bookmarkedCities, setBookmarkedCities] = useState(new Set());
  const [showBookmarksOnly, setShowBookmarksOnly] = useState(false);
  const [bookmarkedResults, setBookmarkedResults] = useState([]);
  const [bookmarkPage, setBookmarkPage] = useState(1);
  const [bookmarkTotal, setBookmarkTotal] = useState(0);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedSalary, setSelectedSalary] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [roleLiveOptions, setRoleLiveOptions] = useState([]);
  const [selectedCrimeRate, setSelectedCrimeRate] = useState([]);
  const [selectedHomePrices, setSelectedHomePrices] = useState([]);
  const [selectedMortgage, setSelectedMortgage] = useState([]);
  const [selectedSafety, setSelectedSafety] = useState([]);
  const [selectedCompact, setSelectedCompact] = useState(null);
  const [selectedRent, setSelectedRent] = useState([]);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [selectedClimate, setSelectedClimate] = useState([]);
  const [upgradeSource, setUpgradeSource] = useState(null);
  const [compareCities, setCompareCities] = useState([]);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const MAX_COMPARE = 3;
  const [selectedSort, setSelectedSort] = useState({
    value: "after_rent-desc",
    label: "Highest Left After Rent",
    sort: "desc",
    field: "after_rent",
  });
  const [subAlertCompModalVisible, setSubAlertCompModalVisible] = useState(
    false,
  );
  const lastTrackedSearchRef = useRef("");
  const authStore = useSelector((state) => state.auth);
  const userStore = useSelector((state) => state.profile);
  const hasFirstSearchCompleted = userStore?.profileItem?.data?.activation_funnel?.progress?.some(
    (step) => step.step === "first_search" && step.completed,
  );

  // "Complete your profile" nudge shown above the results grid — picks the
  // single highest-priority missing field from SEARCH_RELEVANT_PROFILE_NUDGES
  // rather than a generic percentage, since that's more actionable here.
  // Dismissal is in-memory only (not persisted) so the nudge reappears on
  // reload, re-login, or a fresh tab — not just once ever.
  const [profileNudgeDismissed, setProfileNudgeDismissed] = useState(false);
  const searchProfileData = userStore?.profileItem?.data;
  const profileCompletionPercent = getProfileCompletionPercent(
    searchProfileData,
  );
  const activeProfileNudge = searchProfileData
    ? SEARCH_RELEVANT_PROFILE_NUDGES.find((nudge) => {
        const field = PROFILE_COMPLETION_FIELDS_BY_KEY[nudge.key];
        return field && !field.isComplete(searchProfileData);
      })
    : null;
  const dismissProfileNudge = () => {
    setProfileNudgeDismissed(true);
  };
  const hasTrackedRef = useRef(false);

  const [subText, setSubText] = useState({
    header: "",
    desc: "",
    massage1: "",
    pluspricem: "",
    pluspricey: "",
    desc2: "",
    promassage: "",
    propricem: "",
    propricey: "",
  });
  const [subAlertModalVisible, setSubAlertModalVisible] = useState(false);
  const [cityLimitModalVisible, setCityLimitModalVisible] = useState(false);
  const [citySearchLimitVisible, setCitySearchLimitVisible] = useState(false);
  const [loadMoreLimitVisible, setLoadMoreLimitVisible] = useState(false);
  // Records if the user hit a daily limit during this search session, reported to
  // Search Analytics on session end (drives the admin funnel's "Hit Limit" step).
  const hitDailyLimitRef = useRef(false);
  const limitTypeRef = useRef(null);

  // Record a daily-limit hit AS SOON AS the limit modal is shown (so the admin
  // funnel's "Hit Limit" reflects it immediately, not only on session end).
  // session/end updates the existing session row, so calling it here and again
  // on unmount is safe/idempotent.
  const recordLimitHit = (limitType) => {
    hitDailyLimitRef.current = true;
    limitTypeRef.current = limitType;
    const session_id = sessionStorage.getItem("session_id");
    if (session_id) {
      dispatch(
        searchSessionEnd({
          session_id,
          hit_daily_limit: true,
          limit_type: limitType,
        }),
      );
    }
  };

  // Record an upgrade when the user clicks an upgrade CTA inside the limit modal.
  const recordSearchUpgrade = () => {
    const session_id = sessionStorage.getItem("session_id");
    if (session_id) {
      dispatch(searchSessionEnd({ session_id, led_to_upgrade: true }));
    }
  };
  const [showSaveCityModal, setShowSaveCityModal] = useState(false);
  const [cityLimitModalData, setCityLimitModalData] = useState({});
  const [userPlanData, setUserPlanData] = useState({
    plan: "Free",
    expiryDate: "",
  });
  const location = useLocation();
  const isFocus = location.pathname === "/search";

  const [isRestrictedData, setIsRestrictedData] = useState({
    is_comparisan: true,
    comparisanMessage: "",
  });

  // const searchStore = useSelector((state) => state.search);

  const [sortBy, setSortBy] = useState("desc");
  const [fieldName, setFiledName] = useState("after_rent");
  const profileStore = useSelector((state) => state.profile);

  // Free users get a 1/day city-search limit and cannot auto-search by typing.
  const isFreeUser =
    (profileStore?.profileItem?.data?.plan?.slug || "free") === "free";

  // Single source of truth for the search term sent to the API.
  // Free: only the last allowed (gated) search term — typing never leaks through.
  // Plus/Pro: the live query, so as-you-type auto-search is unchanged.
  const apiSearchValue = isFreeUser ? appliedSearch || "" : query?.trim() || "";

  // const { matrix_filter, matrix_filter_2 } = getFeatureAccess(userPlanData);
  const [cleanedFilters, setCleanedFilters] = useState({});
  const cityResults = useSelector(
    (state) => state.search.cityItems?.cities || [],
  );
  const perPageFromStore = useSelector(
    (state) => state.search.cityItems?.total || 0,
  );
  const isSearchLoading = useSelector((state) => state.search.isLoading);
  const filterCityUsage = useSelector(
    (state) => state.search.filterCityUsage || null,
  );

  // Track Activity
  usePageTracking("SEARCH");

  useEffect(() => {
    const payload = {
      page_name: "SEARCH",
      platform: "web",
    };

    dispatch(activityStart(payload));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(
        activityEnd({
          page_name: "SEARCH",
        }),
      );
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllStates());
    dispatch(getAllCities());
    dispatch(getRoles());
    dispatch(getViewProfile());
  }, [dispatch]);

  // Open the bookmarks-only view when the page is entered with
  // `location.state.openBookmarks` set — e.g. via Dashboard's
  // "Your cities › Manage" link. Mirrors the in-page heart toggle
  // (handleSearchBarHeartClick) so the visible state stays consistent.
  // Consumes the flag immediately (replace history state) so a page
  // reload or back/forward doesn't re-trigger it.
  useEffect(() => {
    if (!location.state?.openBookmarks) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await dispatch(getBookmarks({ page: 1, limit: 12 }));
        if (cancelled) return;
        const bookmarks = result?.payload?.data?.data || [];
        const total = result?.payload?.data?.total || 0;
        setBookmarkedResults(bookmarks);
        setBookmarkTotal(total);
        setBookmarkPage(1);
        setShowBookmarksOnly(true);
        setBookmarkedCities(new Set(bookmarks.map((b) => b.id)));
      } catch (error) {
        console.error("Failed to open bookmarks view:", error);
      }
    })();
    // Clear the flag from history so it doesn't fire again on reload.
    navigate(location.pathname, { replace: true, state: {} });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const preserve = sessionStorage.getItem("preserveSearch");
    if (preserve === "true") {
      const saved = JSON.parse(sessionStorage.getItem("searchState"));
      if (saved) {
        setQuery(saved.query || "");
        setSelectedStates(saved.selectedStates || []);
        setSelectedCities(saved.selectedCities || []);
        setSelectedSalary(saved.selectedSalary || []);
        setSelectedRoles(saved.selectedRoles || []);
        setSelectedCrimeRate(saved.selectedCrimeRate || []);
        setSelectedHomePrices(saved.selectedHomePrices || []);
        setSelectedMortgage(saved.selectedMortgage || []);
        setSelectedRent(saved.selectedRent || []);
        setSelectedHospital(saved.selectedHospital || null);
        setSelectedClimate(saved.selectedClimate || []);
        setCleanedFilters(saved.cleanedFilters || {});
        setSelectedSort(saved.selectedSort || sortbyData[0]);
        setSortBy(saved.sortBy || "desc");
        setFiledName(saved.fieldName || "after_rent");
        setCurrentPage(saved.currentPage || 1);
        setIsInitialLoad(true);
      }
      sessionStorage.removeItem("preserveSearch");
      sessionStorage.removeItem("searchState");
    }
  }, []);

  useEffect(() => {
    if (!store?.roleItems) return;

    // const currentPlan = profileStore?.profileItem?.data?.plan?.name;
    const currentPlan = authStore?.user?.plan?.name
      ? authStore?.user?.plan?.name
      : store?.profileItem?.data?.plan?.name;

    const data = store.roleItems.describe_roles.map((s) => {
      const roleName = s.name;

      let isDisabled = false;
      let requiredPlan = "";

      if (currentPlan === "Free") {
        if (!allowedRolesForFree.includes(roleName)) {
          isDisabled = true;
          requiredPlan = "Pro";
        }
      }

      return {
        value: s.id,
        label: roleName,
        isDisabled,
        requiredPlan,
      };
    });

    setRoleLiveOptions(data);
  }, [profileStore, store, store?.profileItem, authStore?.user?.plan?.name]);

  // Default the Role filter to "Registered Nurses" when nothing is selected.
  // Why: the backend already coerces empty role to [9] (RN), and the chip
  // displays "Registered Nurses" as a fallback label — so the popover needs
  // to reflect that selection too. Runs only when roleLiveOptions becomes
  // available; doesn't re-fire after the user clears, because by then
  // selectedRoles will populate again with the default and stay set.
  useEffect(() => {
    if (roleLiveOptions.length === 0) return;
    if (selectedRoles.length > 0) return;
    const rn =
      roleLiveOptions.find((r) => r.value === 9 && !r.isDisabled) ||
      roleLiveOptions.find(
        (r) => /registered\s*nurse/i.test(r.label) && !r.isDisabled,
      );
    if (rn) setSelectedRoles([rn]);
  }, [roleLiveOptions, selectedRoles.length]);

  const _handleCheckCount = async (increment = true, res) => {
    try {
      console.log(res?.id, "res?.id");
      const payload = {
        plan_slug: profileStore?.profileItem?.data?.plan?.slug,
        increment_count: increment,
        city_id: res?.id,
      };

      const result = await dispatch(checkFilterCityCount(payload));
      return result?.payload?.checkFilterCityCountData;
    } catch (error) {
      console.log("Error in checkFilterCityCount:", error);
      return null;
    }
  };
  useEffect(() => {
    if (profileStore?.profileItem) {
      setUserPlanData({
        plan: profileStore?.profileItem?.data?.plan?.name,
        expiryDate: profileStore?.profileItem?.data?.plan_expiry_date,
      });
    }
    setIsRestrictedData({
      is_comparisan: true,
      comparisanMessage: "",
    });
  }, [isFocus, profileStore?.profileItem]);

  const buildFilterPayload = useCallback(() => {
    const filters = {
      crimeRates: selectedCrimeRate.map((c) => c.value),
      homePrices: selectedHomePrices.map((h) => h.value),
      mortgages: selectedMortgage.map((m) => m.value),
      rents: selectedRent.map((r) => r.value),
      cities: selectedCities.map((c) => c.value),
      salaries: selectedSalary.map((s) => s.value),
      roles: selectedRoles.length > 0 ? selectedRoles.map((r) => r.value) : [9], // Default to [9] if no roles selected
      states: selectedStates.map((s) => s.value),
      safety: selectedSafety.map((s) => s.value),
      compact: selectedCompact?.value,
      hospital: selectedHospital?.value,
      climate: selectedClimate.map((c) => c.value),
    };

    const payload = {
      crime_rates: filters.crimeRates,
      home_prices: filters.homePrices,
      mortgage: filters.mortgages,
      rent: filters.rents,
      city: filters.cities,
      salary: filters.salaries,
      role: filters.roles,
      compact_state: filters.compact,
      states: filters.states,
      safety: filters.safety,
      hospital_count: filters.hospital,
      climate: filters.climate,
      search_value: apiSearchValue,
    };

    // const filteredPayload = Object.fromEntries(
    //   Object.entries(payload).filter(([_, v]) => {
    //     if (Array.isArray(v)) return v.length > 0;
    //     if (typeof v === "string") return v.trim() !== "";
    //     return false;
    //   }),
    // );
    const filteredPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => {
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === "string") return v.trim() !== "";
        if (typeof v === "boolean") return true; // ✅ ADD THIS
        return v !== null && v !== undefined;
      }),
    );

    return filteredPayload;
  }, [
    selectedStates,
    selectedCities,
    selectedSalary,
    selectedRoles,
    selectedCrimeRate,
    selectedHomePrices,
    selectedMortgage,
    selectedSafety,
    selectedCompact?.value,
    selectedRent,
    selectedHospital,
    selectedClimate,
    apiSearchValue,
  ]);
  const buildTrackingFilters = useCallback(() => {
    const filters = [];

    if (selectedSalary.length > 0) filters.push("SalaryRange");
    if (selectedCrimeRate.length > 0) filters.push("Crime");
    if (selectedHomePrices.length > 0) filters.push("Housing");
    if (selectedMortgage.length > 0) filters.push("Mortgage");
    if (selectedRent.length > 0) filters.push("Rent");
    if (selectedRoles.length > 0) filters.push("Role");
    if (selectedStates.length > 0) filters.push("State");
    if (selectedCities.length > 0) filters.push("City");

    return filters;
  }, [
    selectedSalary,
    selectedCrimeRate,
    selectedHomePrices,
    selectedMortgage,
    selectedRent,
    selectedRoles,
    selectedStates,
    selectedCities,
  ]);
  const parseMoneyRange = (range) => {
    if (!range) return { min: null, max: null };

    const normalize = (val) => {
      if (val.includes("m")) return parseFloat(val) * 1000000;
      if (val.includes("k")) return parseFloat(val) * 1000;
      return parseFloat(val);
    };

    if (range.includes("+")) {
      const min = normalize(range.replace("+", ""));
      return { min, max: null };
    }

    const [minRaw, maxRaw] = range.split("-");
    return {
      min: normalize(minRaw),
      max: normalize(maxRaw),
    };
  };

  // Compact preview label for a chip: "X selected" for >1, single label for 1, fallback for 0
  const chipLabel = (arr, fallback) => {
    if (!Array.isArray(arr) || arr.length === 0) return fallback;
    if (arr.length === 1) return arr[0].label;
    return `${arr.length} selected`;
  };

  // Open the subscription modal when a user taps a plan-gated role in the chip popover.
  // Mirrors the logic in CustomRoleOption so behaviour stays identical.
  const handleLockedRoleTap = useCallback((opt) => {
    setUpgradeSource("nursing_role_limit");
    if (opt.requiredPlan === "Pro") {
      setSubText({
        header: "Subscription Required!",
        desc: "",
        massage1: "",
        massage2: "",
        desc2: "You need a Pro plan to unlock this role",
        promassage:
          "Pro users see all 9 nursing roles, including NP, CRNA, and Midwife",
        // promassage: "Upgrade to Pro for full role visibility",
        propricem: "$2.99/month",
        propricey: "$29.99/year",
        plan: "Pro",
        lockedItemName: opt.label,
      });
    }
    setSubAlertCompModalVisible(true);
  }, []);

  // Record the current search filters into Search Analytics (the search_events
  // table the admin "Search & Filter Analytics" page reads). Shared so BOTH the
  // quick-filter chips and the "More filters" drawer feed the same dashboard.
  // `total` is the REAL result count (0 when none) so Zero Results Rate is correct.
  const trackSearchFilters = (sel, total) => {
    const searchSessionId = sessionStorage.getItem("session_id");
    if (!searchSessionId) return;

    const homePriceValue = sel.homePrices?.[0]?.value;
    const { min: home_price_min, max: home_price_max } = parseMoneyRange(
      homePriceValue,
    );
    const mortgageValue = sel.mortgage?.[0]?.value;
    const { min: mortgage_min, max: mortgage_max } = parseMoneyRange(
      mortgageValue,
    );
    const salaryValue = sel.salary?.[0]?.value;
    const { min: salary_min, max: salary_max } = parseMoneyRange(salaryValue);
    const rentValue = sel.rent?.[0]?.value;
    const { min: rent_price_min, max: rent_price_max } = parseMoneyRange(
      rentValue,
    );
    const roleValue = sel.roles?.[0]?.label || null;
    const stateValue = sel.states?.[0]?.label || null;
    const cityValue = sel.cities?.[0]?.label || null;
    // Crime is multi-select (low/moderate/high) — send all selected, comma-joined.
    const crimeRateValue =
      Array.isArray(sel.crimeRates) && sel.crimeRates.length
        ? sel.crimeRates.map((c) => c.value).join(",")
        : null;

    const payload = { session_id: searchSessionId, results_count: total };
    if (salaryValue) payload.salary_range = salaryValue;
    if (salary_min !== null) payload.salary_min = salary_min;
    if (salary_max !== null) payload.salary_max = salary_max;
    if (roleValue) payload.role_filter = roleValue;
    if (stateValue) payload.state = stateValue;
    if (crimeRateValue) payload.crime_rate = crimeRateValue;
    if (cityValue) payload.city = cityValue;
    if (homePriceValue) payload.home_price_range = homePriceValue;
    if (home_price_min !== null) payload.home_price_min = home_price_min;
    if (home_price_max !== null) payload.home_price_max = home_price_max;
    if (mortgageValue) payload.mortgage_range = mortgageValue;
    if (mortgage_min !== null) payload.mortgage_min = mortgage_min;
    if (mortgage_max !== null) payload.mortgage_max = mortgage_max;
    if (rentValue) payload.rent_range = rentValue;
    if (rent_price_min !== null) payload.rent_price_min = rent_price_min;
    if (rent_price_max !== null) payload.rent_price_max = rent_price_max;

    // Hospital count (single value), climate & safety (multi → comma-joined string).
    if (sel.hospital) payload.hospital_count = sel.hospital;
    if (Array.isArray(sel.climate) && sel.climate.length)
      payload.climate = sel.climate.join(",");
    if (Array.isArray(sel.safety) && sel.safety.length)
      payload.safety = sel.safety.join(",");
    // Compact State is a yes/no toggle; record it whenever the user has made a
    // choice (true or false), stored as a string.
    if (sel.compact !== undefined && sel.compact !== null)
      payload.compact_state = String(sel.compact);

    dispatch(searchAnalytics(payload));
  };

  // Apply a chip change: setSelectedX(...) and immediately dispatch filterCity
  // with the next value spliced in (can't call applyFilters() because state is async).
  const applyChipChange = async (overrides) => {
    if ("roles" in overrides) setSelectedRoles(overrides.roles);
    if ("crimeRates" in overrides) setSelectedCrimeRate(overrides.crimeRates);
    if ("compact" in overrides) setSelectedCompact(overrides.compact);
    if ("homePrices" in overrides) setSelectedHomePrices(overrides.homePrices);

    const roles = "roles" in overrides ? overrides.roles : selectedRoles;
    const crimeRates =
      "crimeRates" in overrides ? overrides.crimeRates : selectedCrimeRate;
    const compact =
      "compact" in overrides ? overrides.compact : selectedCompact;
    const homePrices =
      "homePrices" in overrides ? overrides.homePrices : selectedHomePrices;

    const payload = {
      crime_rates: crimeRates.map((c) => c.value),
      home_prices: homePrices.map((h) => h.value),
      mortgage: selectedMortgage.map((m) => m.value),
      rent: selectedRent.map((r) => r.value),
      city: selectedCities.map((c) => c.value),
      salary: selectedSalary.map((s) => s.value),
      role: roles.length > 0 ? roles.map((r) => r.value) : [9],
      compact_state: compact?.value,
      states: selectedStates.map((s) => s.value),
      safety: selectedSafety.map((s) => s.value),
      search_value: apiSearchValue,
    };
    const cleaned = Object.fromEntries(
      Object.entries(payload).filter(([, v]) => {
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === "string") return v.trim() !== "";
        if (typeof v === "boolean") return true;
        return v != null;
      }),
    );

    setCleanedFilters(cleaned);
    setCurrentPage(1);

    const result = await dispatch(
      filterCity({
        ...cleaned,
        search_value: apiSearchValue,
        shortBy: sortBy,
        fieldName,
        page: 1,
        append: false,
        role: cleaned.role && cleaned.role.length > 0 ? cleaned.role : [9],
      }),
    );

    // Record chip filters into Search Analytics (search_events) with the REAL total
    // result count. Previously only the "More filters" drawer did this, so chip usage
    // (the primary filters) was invisible to the admin Search & Filter Analytics page.
    trackSearchFilters(
      {
        roles,
        crimeRates,
        homePrices,
        mortgage: selectedMortgage,
        rent: selectedRent,
        cities: selectedCities,
        salary: selectedSalary,
        states: selectedStates,
        hospital: selectedHospital?.value,
        climate: selectedClimate.map((c) => c.value),
        safety: selectedSafety.map((s) => s.value),
        compact: compact?.value,
      },
      result?.payload?.total || 0,
    );

    // Analytics — match the drawer's filter_applied event so dashboards stay consistent
    const session_id = sessionStorage.getItem("comparison_session_id");
    if (session_id) {
      const tracked = [];
      if (roles.length > 0) tracked.push("Role");
      if (crimeRates.length > 0) tracked.push("Crime");
      if (homePrices.length > 0) tracked.push("Housing");
      if (compact?.value !== undefined) tracked.push("Compact");
      if (tracked.length > 0) {
        dispatch(
          ComparisonTrack({
            event_type: "filter_applied",
            event_data: { session_id, filters: tracked },
          }),
        );
      }
    }
  };

  const applyFilters = useCallback(async () => {
    const payload = buildFilterPayload();
    setCleanedFilters(payload);
    setCurrentPage(1);
    setShowFilters(false);

    const session_id = sessionStorage.getItem("comparison_session_id");
    const filters = buildTrackingFilters();

    if (session_id && filters.length > 0) {
      dispatch(
        ComparisonTrack({
          event_type: "filter_applied",
          event_data: {
            session_id,
            filters,
          },
        }),
      );
    }

    const result = await dispatch(
      filterCity({
        ...payload,
        search_value: apiSearchValue,
        shortBy: sortBy,
        fieldName,
        page: 1,
        append: false,
        // Add default role [9] if no role filter exists
        role: payload.role && payload.role.length > 0 ? payload.role : [9],
      }),
    );

    const updatedTotal = result?.payload?.total || 0;

    // Record the full drawer filter state (incl. hospital/climate/safety) with the
    // real total result count via the shared helper, so chips and drawer feed the
    // admin Search & Filter Analytics identically.
    trackSearchFilters(
      {
        roles: selectedRoles,
        crimeRates: selectedCrimeRate,
        homePrices: selectedHomePrices,
        mortgage: selectedMortgage,
        rent: selectedRent,
        cities: selectedCities,
        salary: selectedSalary,
        states: selectedStates,
        hospital: selectedHospital?.value,
        climate: selectedClimate.map((c) => c.value),
        safety: selectedSafety.map((s) => s.value),
        compact: selectedCompact?.value,
      },
      updatedTotal,
    );
  }, [
    buildFilterPayload,
    buildTrackingFilters,
    apiSearchValue,
    sortBy,
    fieldName,
    dispatch,
    selectedHomePrices,
    selectedRoles,
    selectedMortgage,
    selectedCrimeRate,
    selectedRent,
    selectedSalary,
    selectedStates,
    selectedCities,
    selectedHospital,
    selectedClimate,
    selectedSafety,
    // selectedSort,
  ]);

  const handleClearFilters = () => {
    setSelectedStates([]);
    setSelectedCities([]);
    setSelectedSalary([]);
    setSelectedRoles([]);
    setSelectedSafety([]);
    setSelectedCrimeRate([]);
    setSelectedHomePrices([]);
    setSelectedMortgage([]);
    setSelectedRent([]);
    setSelectedCompact(null);
    setSelectedHospital(null);
    setSelectedClimate([]);
    setCleanedFilters({});
    setCurrentPage(1);

    dispatch(
      filterCity({
        role: [9], // Always include default role
        search_value: apiSearchValue,
        shortBy: sortBy,
        fieldName,
        page: 1,
        append: false,
      }),
    );
  };
  const user = useSelector((state) => state.auth?.user);

  useEffect(() => {
    if (!user) {
      sessionStorage.removeItem("comparison_session_id");
      sessionStorage.removeItem("session_expiry");

      console.log("🧹 Session cleared on logout");
    }
  }, [user]);
  useEffect(() => {
    if (!user?.id) return;

    const existingSession = sessionStorage.getItem("comparison_session_id");
    const expiry = sessionStorage.getItem("session_expiry");

    // 🔥 Check if valid session exists
    if (existingSession && expiry && Date.now() < Number(expiry)) {
      return;
    }

    // const token = localStorage.getItem("authToken");

    // dispatch(
    //   sessionId({
    //     user_id: user.id,
    //     access_token: token,
    //   }),
    dispatch(sessionId()).then((res) => {
      const data = res.payload?.data;

      if (data?.session_id) {
        sessionStorage.setItem("comparison_session_id", data.session_id);

        // 🔥 store expiry (important)
        const expiryTime = Date.now() + data.expires_in * 1000;
        sessionStorage.setItem("session_expiry", expiryTime);
      }
    });
  }, [user]);
  const sessionCreatedRef = useRef(false);

  useEffect(() => {
    if (!user?.id) return;

    if (location.pathname === "/search" && !sessionCreatedRef.current) {
      sessionCreatedRef.current = true;

      sessionStorage.removeItem("session_id");

      dispatch(searchSessionId()).then((res) => {
        const data = res.payload?.data;

        if (data?.session_id) {
          sessionStorage.setItem("session_id", data.session_id);
        }
      });
    }
  }, [location.pathname, user]);

  // const handleLoadMore = async () => {
  //   if (showBookmarksOnly) {
  //     // Load more bookmarks
  //     const nextPage = bookmarkPage + 1;
  //     try {
  //       const result = await dispatch(
  //         getBookmarks({ page: nextPage, limit: 12 }),
  //       );
  //       const newBookmarks = result?.payload?.data?.data || [];

  //       // Append new bookmarks to existing ones
  //       setBookmarkedResults((prev) => [...prev, ...newBookmarks]);
  //       setBookmarkPage(nextPage);

  //       // Update bookmarked cities set
  //       const newBookmarkedIds = new Set([
  //         ...bookmarkedCities,
  //         ...newBookmarks.map((b) => b.id),
  //       ]);
  //       setBookmarkedCities(newBookmarkedIds);
  //     } catch (error) {
  //       console.error("Failed to load more bookmarks:", error);
  //     }
  //     return;
  //   }

  //   // Original load more for search results
  //   const planSlug = profileStore?.profileItem?.data?.plan?.slug;

  //   if (isFocus && ["free", "plus"].includes(planSlug)) {
  //     const restrictionData = await _handleCheckCount(true);

  //     if (
  //       restrictionData?.is_comparisan === false &&
  //       restrictionData?.used_count >= restrictionData?.comparisanMaxCount &&
  //       restrictionData?.incrementSuccess === false
  //     ) {
  //       setSubText({
  //         header: restrictionData.header || "Subscription Required!",
  //         desc:
  //           restrictionData.comparisanMessage ||
  //           "Upgrade to Pro for unlimited city exploration.",
  //       });
  //       setUpgradeSource("city_load_more_limit");
  //       setSubAlertModalVisible(true);
  //       setIsRestrictedData(restrictionData);
  //       return;
  //     }

  //     setIsRestrictedData(restrictionData);
  //   }

  //   const nextPage = currentPage + 1;
  //   dispatch(
  //     filterCity({
  //       ...cleanedFilters,
  //       search_value: query?.trim() || "",
  //       shortBy: selectedSort.sort,
  //       fieldName: selectedSort.field,
  //       page: nextPage,
  //       append: true,
  //     }),
  //   );
  //   setCurrentPage(nextPage);
  // };
  const handleLoadMore = async () => {
    if (loadMoreLoading) return; // ✅ prevent double click

    setLoadMoreLoading(true);

    try {
      if (showBookmarksOnly) {
        const nextPage = bookmarkPage + 1;

        const result = await dispatch(
          getBookmarks({ page: nextPage, limit: 12 }),
        );

        const newBookmarks = result?.payload?.data?.data || [];

        setBookmarkedResults((prev) => [...prev, ...newBookmarks]);
        setBookmarkPage(nextPage);

        const newBookmarkedIds = new Set([
          ...bookmarkedCities,
          ...newBookmarks.map((b) => b.id),
        ]);
        setBookmarkedCities(newBookmarkedIds);

        return; // ✅ keep your logic
      }

      // 🔥 restriction logic (unchanged)
      const planSlug = profileStore?.profileItem?.data?.plan?.slug;

      if (isFocus && planSlug === "free") {
        const restrictionData = await _handleCheckCount(true);

        if (
          restrictionData?.is_comparisan === false &&
          restrictionData?.used_count >= restrictionData?.comparisanMaxCount &&
          restrictionData?.incrementSuccess === false
        ) {
          setSubText({
            header: restrictionData.header || "Subscription Required!",
            desc:
              restrictionData.comparisanMessage ||
              "Upgrade to Pro for unlimited city exploration.",
          });
          setUpgradeSource("city_load_more_limit");
          recordLimitHit("city_load_more");
          setLoadMoreLimitVisible(true);
          setIsRestrictedData(restrictionData);

          return; // ❗ stop loader in finally
        }

        setIsRestrictedData(restrictionData);
      }

      const nextPage = currentPage + 1;

      // Build payload and ensure default role [9] is included
      const payload = {
        ...cleanedFilters,
        search_value: apiSearchValue,
        shortBy: selectedSort.sort,
        fieldName: selectedSort.field,
        page: nextPage,
        append: true,
      };

      // Add default role [9] if no role filter exists
      if (!payload.role || payload.role.length === 0) {
        payload.role = [9];
      }

      await dispatch(filterCity(payload));

      setCurrentPage(nextPage);
    } catch (error) {
      console.error("Failed to load more:", error);
    } finally {
      setLoadMoreLoading(false); // ✅ ALWAYS reset
    }
  };
  const handleSearchClick = async (res) => {
    const planSlug = profileStore?.profileItem?.data?.plan?.slug;
    if (planSlug === "free") {
      const restrictionData = await _handleCheckCount(true, res);

      if (
        restrictionData?.is_comparisan === false &&
        restrictionData?.used_count >= restrictionData?.comparisanMaxCount &&
        restrictionData?.incrementSuccess === false
      ) {
        setUpgradeSource("city_detail_limit");
        // Show the paywall design's fixed copy — the API's
        // header/comparisanMessage shouldn't override it.
        setCityLimitModalData({});
        recordLimitHit("city_detail");
        setCityLimitModalVisible(true);
        setIsRestrictedData(restrictionData);
        return;
      }
    }

    const stateToSave = {
      query,
      selectedStates,
      selectedCities,
      selectedSalary,
      selectedRoles,
      selectedCrimeRate,
      selectedHomePrices,
      selectedMortgage,
      selectedRent,
      selectedHospital,
      selectedClimate,
      cleanedFilters,
      selectedSort,
      sortBy,
      fieldName,
      currentPage,
    };
    sessionStorage.setItem("searchState", JSON.stringify(stateToSave));
    sessionStorage.setItem("preserveSearch", "true");

    const searchSessionId = sessionStorage.getItem("session_id");

    if (searchSessionId) {
      dispatch(
        trackCityClick({
          session_id: searchSessionId,
          city_name: `${res.city}, ${res.state}`, // adjust based on your res structure
        }),
      );
    }

    navigate(`/search/details/${res.id}`, {
      state: { from: "search", searchData: res },
    });
  };
  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false);
      setLoading(true);

      // Build payload with default role [9] if no roles are selected
      const initialPayload = {
        ...cleanedFilters,
        search_value: apiSearchValue,
        shortBy: sortBy,
        fieldName,
        page: 1,
        append: false,
      };

      // Add default role [9] if no role filter exists
      if (!initialPayload.role || initialPayload.role.length === 0) {
        initialPayload.role = [9];
      }

      dispatch(filterCity(initialPayload)).finally(() => {
        setLoading(false);
      });
      setCurrentPage(1);
      return;
    }

    // Don't trigger if there are no filters, no query, and it's not the initial load
    // Allow sort changes to trigger the API call
    // if (!hasFilters && !hasQuery) return;

    const timer = setTimeout(() => {
      const cleanedQuery = apiSearchValue;
      setLoading(true);

      // 🔥 META Search Event (only if real query exists)
      if (
        cleanedQuery &&
        lastTrackedSearchRef.current !== cleanedQuery &&
        typeof window !== "undefined" &&
        window.fbq
      ) {
        lastTrackedSearchRef.current = cleanedQuery;

        window.fbq("track", "Search", {
          search_string: cleanedQuery,
        });
      }

      // Build payload and ensure default role [9] is included
      const payload = {
        ...cleanedFilters,
        search_value: cleanedQuery,
        shortBy: sortBy,
        fieldName,
        page: 1,
        append: false,
      };

      // Add default role [9] if no role filter exists
      if (!payload.role || payload.role.length === 0) {
        payload.role = [9];
      }

      dispatch(filterCity(payload)).finally(() => {
        setLoading(false);
      });

      setCurrentPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [
    cleanedFilters,
    apiSearchValue,
    sortBy,
    fieldName,
    isInitialLoad,
    dispatch,
  ]);

  const CustomRoleOption = (props) => {
    const { isDisabled, data } = props;
    const { requiredPlan } = data;

    return (
      <div
        onClick={(e) => {
          if (isDisabled) {
            e.stopPropagation();
            const source = "nursing_role_limit";
            setUpgradeSource(source);
            if (requiredPlan === "Pro") {
              setSubText({
                header: "Subscription Required!",
                desc: "",
                massage1: "",
                massage2: "",
                desc2: "You need a Pro plan to unlock this role",
                promassage:
                  "Pro users see all 9 nursing roles, including NP, CRNA, and Midwife",
                // promassage: "Upgrade to Pro for full role visibility",
                propricem: "$2.99/month",
                propricey: "$29.99/year",
              });
            }

            setSubAlertCompModalVisible(true);
          }
        }}
        style={{
          width: "100%",
          cursor: isDisabled ? "pointer" : "default",
        }}
      >
        <components.Option {...props} />
      </div>
    );
  };
  useEffect(() => {
    const searchSessionId = sessionStorage.getItem("session_id");
    if (!searchSessionId) return;

    const trimmedQuery = query?.trim();
    if (!trimmedQuery) return;

    // Wait until the search has finished loading, then record the result count —
    // including 0, so zero-result searches are captured. (A previous `!total`
    // guard here silently dropped every zero-result search.)
    if (isSearchLoading) return;

    const timer = setTimeout(() => {
      dispatch(
        searchAnalytics({
          session_id: searchSessionId,
          city_search_term: trimmedQuery,
          results_count: perPageFromStore,
          // sort_method: selectedSort?.track || "",
        }),
      );
    }, 2000);

    return () => clearTimeout(timer);
  }, [perPageFromStore, isSearchLoading, query, dispatch, selectedSort?.track]);

  // End the search session when the user leaves the page (unmount), reporting
  // whether a daily limit was hit during the session so the admin funnel's
  // "Hit Limit" step populates. (Previously this fired on mount and sent only
  // session_id, so hit_daily_limit was never recorded.)
  useEffect(() => {
    return () => {
      const session_id = sessionStorage.getItem("session_id");
      if (session_id) {
        dispatch(
          searchSessionEnd({
            session_id,
            hit_daily_limit: hitDailyLimitRef.current,
            limit_type: limitTypeRef.current || undefined,
          }),
        );
      }
    };
  }, [dispatch]);
  const formatPercent = (val) => {
    if (!val) return 0;
    const num = parseFloat(String(val).replace("%", ""));
    return isNaN(num) ? 0 : num;
  };
  const handleCompareClick = (city) => {
    setCompareCities((prev) => {
      // already added → ignore
      if (prev.find((c) => c.id === city.id)) return prev;

      // limit 3
      if (prev.length >= MAX_COMPARE) return prev;

      return [...prev, city];
    });
  };
  const removeCompareCity = (id) => {
    setCompareCities((prev) => prev.filter((c) => c.id !== id));
  };
  const getBarClass = (value) => {
    if (value < 0) return "negative";
    // if (value < 1000) return "warning"; // optional
    return "positive";
  };

  const handleBookmarkToggle = async (e, city) => {
    e.stopPropagation();

    try {
      await dispatch(
        searchBookmark({
          city: city.city,
          state: city.state,
          role: city.role,
        }),
      ).unwrap();

      // Only update UI state when the API call succeeds
      setBookmarkedCities((prev) => {
        const newSet = new Set(prev);
        const isRemoving = newSet.has(city.id);

        if (isRemoving) {
          newSet.delete(city.id);
        } else {
          newSet.add(city.id);
        }

        // If in bookmarks view, update total count
        if (showBookmarksOnly) {
          if (isRemoving) {
            setBookmarkTotal((prev) => Math.max(0, prev - 1));
          } else {
            setBookmarkTotal((prev) => prev + 1);
          }
        }

        return newSet;
      });

      // If in bookmarks view, remove from the list
      if (showBookmarksOnly) {
        setBookmarkedResults((prev) =>
          prev.filter(
            (b) =>
              !(
                b.city === city.city &&
                b.state === city.state &&
                b.role === city.role
              ),
          ),
        );
      }
    } catch (error) {
      if (error?.status === 403) {
        setShowSaveCityModal(true);
      } else {
        console.error("Bookmark toggle failed:", error);
      }
    }
  };

  // Check bookmark status when cities load
  useEffect(() => {
    // Skip if showing bookmarks only
    if (showBookmarksOnly) return;
    if (cityResults.length === 0) return;

    const checkBookmarks = async () => {
      try {
        const result = await dispatch(getBookmarks({ page: 1, limit: 12 }));
        const bookmarkedData = result?.payload?.data?.data || [];

        // Loop through cityResults and check if each city is bookmarked
        const bookmarkedIds = new Set();

        cityResults.forEach((city) => {
          const isBookmarked = bookmarkedData.some(
            (bookmark) =>
              bookmark.city === city.city &&
              bookmark.state === city.state &&
              bookmark.role === city.role,
          );

          if (isBookmarked) {
            bookmarkedIds.add(city.id);
          }
        });

        setBookmarkedCities(bookmarkedIds);
      } catch (error) {
        console.error("Failed to check bookmarks:", error);
      }
    };

    checkBookmarks();
  }, [cityResults, dispatch, showBookmarksOnly]);

  const handleSearchBarHeartClick = async () => {
    if (!showBookmarksOnly) {
      // Show bookmarks only
      try {
        const result = await dispatch(getBookmarks({ page: 1, limit: 12 }));
        const bookmarks = result?.payload?.data?.data || [];
        const total = result?.payload?.data?.total || 0;
        setBookmarkedResults(bookmarks);
        setBookmarkTotal(total);
        setBookmarkPage(1);
        setShowBookmarksOnly(true);

        // Mark all bookmarked results as bookmarked
        const bookmarkedIds = new Set(bookmarks.map((b) => b.id));
        setBookmarkedCities(bookmarkedIds);
      } catch (error) {
        console.error("Failed to fetch bookmarks:", error);
      }
    } else {
      // Show all results
      setShowBookmarksOnly(false);
      setBookmarkPage(1);
    }
  };
  const CRIME_COLOR_MAP = {
    mild: "var(--mmp-success)",
    moderate: "var(--mmp-warning)",
    severe: "var(--mmp-danger)",
    unavailable: "var(--mmp-text-secondary)",

    // optional (if API sends colors sometimes)
    green: "var(--mmp-success)",
    yellow: "var(--mmp-warning)",
    orange: "var(--mmp-warning)",
    red: "var(--mmp-danger)",
    gray: "var(--mmp-text-secondary)",
  };
  // Pastel crime pill styles for the redesigned result card
  const CRIME_PILL_MAP = {
    mild: { background: "var(--mmp-success-bg)", color: "var(--mmp-success)" },
    low: { background: "var(--mmp-success-bg)", color: "var(--mmp-success)" },
    green: { background: "var(--mmp-success-bg)", color: "var(--mmp-success)" },
    moderate: { background: "var(--mmp-warning-bg)", color: "var(--mmp-warning)" },
    yellow: { background: "var(--mmp-warning-bg)", color: "var(--mmp-warning)" },
    orange: { background: "var(--mmp-warning-bg)", color: "var(--mmp-warning)" },
    severe: { background: "var(--mmp-danger-bg)", color: "var(--mmp-danger)" },
    high: { background: "var(--mmp-danger-bg)", color: "var(--mmp-danger)" },
    red: { background: "var(--mmp-danger-bg)", color: "var(--mmp-danger)" },
    unavailable: { background: "var(--mmp-hover-bg)", color: "var(--mmp-text-secondary)" },
    gray: { background: "var(--mmp-hover-bg)", color: "var(--mmp-text-secondary)" },
  };
  const getTaxStyle = (val) => {
    const percent = parseFloat(String(val || 0).replace("%", ""));

    if (percent < 25) {
      return {
        background: "var(--mmp-success-bg)",
        border: "1px solid var(--mmp-success-border)",
        color: "var(--mmp-success)",
      };
    }

    if (percent <= 35) {
      return {
        background: "var(--mmp-warning-bg)",
        border: "1px solid var(--mmp-warning-border)",
        color: "var(--mmp-warning)",
      };
    }

    return {
      background: "var(--mmp-danger-bg)",
      border: "1px solid var(--mmp-danger-border)",
      color: "var(--mmp-danger)",
    };
  };
  const formatNumber = (num) => {
    if (!num) return "";

    const n = Number(num);

    if (n >= 1_000_000) {
      return (n / 1_000_000).toFixed(1).replace(".0", "") + "M";
    }

    if (n >= 1_000) {
      return (n / 1_000).toFixed(1).replace(".0", "") + "K";
    }

    return n;
  };
  const formatMoney = (val) => {
    if (!val) return "";

    const num = Number(val);
    if (isNaN(num)) return "";

    const abs = Math.abs(num);

    if (abs >= 1_000_000) {
      return (abs / 1_000_000).toFixed(1).replace(".0", "") + "M";
    }

    if (abs >= 1_000) {
      return (abs / 1_000).toFixed(1).replace(".0", "") + "K";
    }

    return abs.toLocaleString("en-US");
  };

  // Debounced autocomplete: fetch City, State suggestions as the user types.
  useEffect(() => {
    const term = query?.trim();
    if (!term) {
      setSuggestions([]);
      setActiveSuggestion(-1);
      return undefined;
    }

    const timer = setTimeout(() => {
      dispatch(cityAutocomplete({ search: term }))
        .unwrap()
        .then((data) => {
          setSuggestions(Array.isArray(data) ? data : []);
          setActiveSuggestion(-1);
        })
        .catch(() => {
          setSuggestions([]);
          setActiveSuggestion(-1);
        });
    }, 250);

    return () => clearTimeout(timer);
  }, [query, dispatch]);

  // Close the suggestions dropdown when clicking outside the search input.
  useEffect(() => {
    if (!showSuggestions) return undefined;
    const onDown = (e) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showSuggestions]);

  const handlePickSuggestion = async (s) => {
    const term = `${s.city}, ${s.state}`;
    setQuery(term);
    setShowSuggestions(false);
    setActiveSuggestion(-1);

    // Plus/Pro: picking a suggestion auto-searches via apiSearchValue (= query).
    if (!isFreeUser) return;

    // Free users: a suggestion click is their ONLY search path, capped at 1/day.
    // Check + increment the daily counter; block the 2nd attempt with the
    // upgrade modal (same flag contract as the city-detail / load-more limits).
    try {
      const res = await dispatch(
        checkCitySearch({
          plan_slug: "free",
          increment_count: true,
          search_term: term,
        }),
      );
      const data = res?.payload?.checkCitySearchData;
      const blocked =
        data && data.is_comparisan === false && data.incrementSuccess === false;

      if (blocked) {
        recordLimitHit("city_search");
        setCitySearchLimitVisible(true);
        setQuery(appliedSearch); // revert the box to the last applied search
        return;
      }

      // Allowed — apply the term so the search effect fetches results.
      setAppliedSearch(term);
      setCurrentPage(1);
    } catch (error) {
      console.error("City search limit check failed:", error);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggestion((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestion((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      if (activeSuggestion >= 0 && activeSuggestion < suggestions.length) {
        e.preventDefault();
        handlePickSuggestion(suggestions[activeSuggestion]);
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setActiveSuggestion(-1);
    }
  };
  return (
    <div className="search-content">
      <NoIndexSEO title={seoConfig.search.title} />

      {activeProfileNudge && !profileNudgeDismissed && (
        <div
          className="search-profile-nudge"
          role="button"
          tabIndex={0}
          onClick={() => navigate("/profile")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate("/profile");
            }
          }}
        >
          <div className="search-profile-nudge-icon">
            <UserRound />
          </div>
          <div className="search-profile-nudge-body">
            <div className="search-profile-nudge-head">
              <span className="search-profile-nudge-eyebrow">
                Complete your profile
              </span>
              <span className="search-profile-nudge-pct">
                {profileCompletionPercent}%
              </span>
            </div>
            <div className="search-profile-nudge-bar-track">
              <div
                className="search-profile-nudge-bar-fill"
                style={{ width: `${profileCompletionPercent}%` }}
              />
            </div>
            <span className="search-profile-nudge-text">
              {activeProfileNudge.message}
            </span>
          </div>
          <button
            type="button"
            className="search-profile-nudge-close"
            aria-label="Dismiss"
            onClick={(e) => {
              e.stopPropagation();
              dismissProfileNudge();
            }}
          >
            ×
          </button>
        </div>
      )}

      <div className="app-page-title">
        <div className="page-title-heading">
          <h3 className="page-title">Search Cities</h3>
          <div className="page-subtitle mt-1">
            Real take-home pay, taxes, and housing costs for nurses across
            <b style={{ color: "var(--mmp-text-primary)" }}> 21,000+ </b>cities
          </div>
        </div>
        {/* <div className="page-title-heading mt-2">
          <h3 className="page-title">Search Cities</h3>

        </div> */}
      </div>

      {/* Search Header */}
      <div className="search-header">
        <div className="search-input" ref={searchInputRef} data-tour="search-bar">
          <FiSearch className="icon" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              const value = e.target.value;
              setQuery(value);
              setShowSuggestions(true);

              // Clearing the box brings back the default list. For Free users
              // results are driven by appliedSearch (not the live query), so
              // reset it here too — this doesn't call checkCitySearch, so it
              // neither consumes nor refunds the daily search count.
              if (!value.trim() && appliedSearch) {
                setAppliedSearch("");
              }

              if (hasFirstSearchCompleted) return;

              if (hasTrackedRef.current) return;

              hasTrackedRef.current = true;

              dispatch(
                activationTrack({
                  event_name: "first_search",
                  platform: "web",
                }),
              );
            }}
            onFocus={() => {
              if (query?.trim()) setShowSuggestions(true);
            }}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search by city, salary & role ..."
          />
          <FiHeart
            className={`heart-icon ${showBookmarksOnly ? "active" : ""}`}
            data-tour="search-fav"
            onClick={handleSearchBarHeartClick}
          />
          {showSuggestions && suggestions.length > 0 && (
            <div className="search-suggestions" role="listbox">
              {suggestions.map((s, i) => (
                <button
                  key={`${s.city}-${s.state}-${i}`}
                  type="button"
                  className={`search-suggestion-item ${
                    i === activeSuggestion ? "is-active" : ""
                  }`}
                  role="option"
                  aria-selected={i === activeSuggestion}
                  onMouseEnter={() => setActiveSuggestion(i)}
                  onClick={() => handlePickSuggestion(s)}
                >
                  {s.city}, {s.state}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick-filter chip row + quota pill (new design) */}
      {!showBookmarksOnly && (
        <div className="mmp-search-topbar" data-tour="search-filterbar">
          <div className="chip-row" role="toolbar" aria-label="Quick filters">
            <ChipDropdown
              label="Role"
              dataTour="search-filter-role"
              value={chipLabel(selectedRoles, "Registered Nurses")}
              active /* role is always considered set (default = RN) */
              options={roleLiveOptions}
              selected={selectedRoles}
              isMulti
              isClearable
              onApply={(next) => applyChipChange({ roles: next })}
              onLockedTap={handleLockedRoleTap}
            />
            <ChipDropdown
              label="Crime"
              dataTour="search-filter-crime"
              value={chipLabel(selectedCrimeRate, "All")}
              active={selectedCrimeRate.length > 0}
              options={crimeRateOptions}
              selected={selectedCrimeRate}
              isMulti
              isClearable
              onApply={(next) => applyChipChange({ crimeRates: next })}
            />
            <ChipDropdown
              label="Compact State"
              dataTour="search-filter-compact"
              value={selectedCompact?.label || "Off"}
              active={!!selectedCompact}
              options={compactOptions}
              selected={selectedCompact}
              isClearable
              onApply={(next) => applyChipChange({ compact: next })}
            />
            <ChipDropdown
              label="Home Price"
              dataTour="search-filter-home"
              value={chipLabel(selectedHomePrices, "Any")}
              active={selectedHomePrices.length > 0}
              options={homePriceOptions}
              selected={selectedHomePrices}
              isMulti
              isClearable
              onApply={(next) => applyChipChange({ homePrices: next })}
            />
            <button
              type="button"
              className={`chip chip-more ${showFilters ? "is-active" : ""}`}
              data-tour="search-morefilters"
              onClick={() => setShowFilters((v) => !v)}
              aria-expanded={showFilters}
            >
              <FiSliders className="funnel-icon" />
              <span>{showFilters ? "Hide filters" : "More filters"}</span>
              <ChipChev />
            </button>
          </div>
        </div>
      )}

      {/* Filters Section */}
      {!showBookmarksOnly && showFilters && (
        <div className="filters-section">
          <div className="row">
            {/* Filter by State */}
            <div className="col-md-6 col-lg-4">
              <Form.Group className="form-group">
                <Form.Label className="col-label">Filter by State</Form.Label>
                <Select
                  isMulti
                  value={selectedStates}
                  onChange={setSelectedStates}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  options={[
                    { value: "", label: "Select State" },
                    ...(store?.stateItems?.states?.map((state) => ({
                      value: state.state,
                      label: state.state_full_name,
                    })) || []),
                  ]}
                  placeholder="Select states..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPosition="fixed"
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "var(--mmp-surface)",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "var(--mmp-accent-bg)",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                      "&:hover": {
                        backgroundColor: "var(--mmp-accent-border)",
                        color: "var(--mmp-text-primary)",
                      },
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "var(--mmp-accent-bg)" : "var(--mmp-surface)",
                      color: "var(--mmp-text-primary)",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "var(--mmp-accent-bg)",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </div>

            {/* Filter by City */}
            <div className="col-md-6 col-lg-4">
              <Form.Group className="form-group">
                <Form.Label className="col-label">Filter by City</Form.Label>
                <Select
                  isMulti
                  value={selectedCities}
                  onChange={setSelectedCities}
                  options={[
                    { value: "", label: "Select City" },
                    ...(store?.cityItems?.cities?.map((city) => ({
                      value: city.city,
                      label: city.city,
                    })) || []),
                  ]}
                  placeholder="Select cities..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isSearchable={true}
                  menuPosition="fixed"
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "var(--mmp-surface)",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "var(--mmp-accent-bg)",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                      "&:hover": {
                        backgroundColor: "var(--mmp-accent-border)",
                        color: "var(--mmp-text-primary)",
                      },
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "var(--mmp-accent-bg)" : "var(--mmp-surface)",
                      color: "var(--mmp-text-primary)",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "var(--mmp-accent-bg)",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </div>

            {/* Filter by Salary */}
            <div className="col-md-6 col-lg-4">
              <Form.Group className="form-group">
                <Form.Label className="col-label">Filter by Salary</Form.Label>
                <Select
                  isMulti
                  value={selectedSalary}
                  onChange={setSelectedSalary}
                  options={salaryOptions}
                  placeholder="Select salary range..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPosition="fixed"
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "var(--mmp-surface)",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "var(--mmp-accent-bg)",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                      "&:hover": {
                        backgroundColor: "var(--mmp-accent-border)",
                        color: "var(--mmp-text-primary)",
                      },
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "var(--mmp-accent-bg)" : "var(--mmp-surface)",
                      color: "var(--mmp-text-primary)",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "var(--mmp-accent-bg)",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </div>

            {/* Filter by Role */}
            <div className="col-md-6 col-lg-4">
              <Form.Group className="form-group">
                <Form.Label className="col-label">Filter by Role</Form.Label>
                <Select
                  isMulti
                  value={selectedRoles}
                  onChange={setSelectedRoles}
                  components={{ Option: CustomRoleOption }}
                  options={roleLiveOptions}
                  placeholder="Select roles..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPosition="fixed"
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "var(--mmp-surface)",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "var(--mmp-accent-bg)",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                      "&:hover": {
                        backgroundColor: "var(--mmp-accent-border)",
                        color: "var(--mmp-text-primary)",
                      },
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "var(--mmp-accent-bg)" : "var(--mmp-surface)",
                      color: "var(--mmp-text-primary)",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "var(--mmp-accent-bg)",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </div>

            {/* Filter by Crime Rate */}
            <div className="col-md-6 col-lg-4">
              <Form.Group className="form-group">
                <Form.Label className="col-label">
                  Filter by Crime Rate
                </Form.Label>
                <Select
                  isMulti
                  value={selectedCrimeRate}
                  onChange={setSelectedCrimeRate}
                  options={crimeRateOptions}
                  placeholder="Select crime rate..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPosition="fixed"
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "var(--mmp-surface)",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "var(--mmp-accent-bg)",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                      "&:hover": {
                        backgroundColor: "var(--mmp-accent-border)",
                        color: "var(--mmp-text-primary)",
                      },
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "var(--mmp-accent-bg)" : "var(--mmp-surface)",
                      color: "var(--mmp-text-primary)",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "var(--mmp-accent-bg)",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </div>
            {/* Filter by Safety */}
            <div className="col-md-6 col-lg-4">
              <Form.Group className="form-group">
                <Form.Label className="col-label">Filter by Safety</Form.Label>
                <Select
                  isMulti
                  value={selectedSafety}
                  onChange={setSelectedSafety}
                  options={saftyOptions}
                  placeholder="Select safety..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPosition="fixed"
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "var(--mmp-surface)",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "var(--mmp-accent-bg)",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                      "&:hover": {
                        backgroundColor: "var(--mmp-accent-border)",
                        color: "var(--mmp-text-primary)",
                      },
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "var(--mmp-accent-bg)" : "var(--mmp-surface)",
                      color: "var(--mmp-text-primary)",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "var(--mmp-accent-bg)",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </div>

            {/* Filter by Compact State */}
            <div className="col-md-6 col-lg-4">
              <Form.Group className="form-group">
                <Form.Label className="col-label">Compact State</Form.Label>
                <Select
                  value={selectedCompact}
                  onChange={setSelectedCompact}
                  options={compactOptions}
                  placeholder="Select compact state..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPosition="fixed"
                  isMulti={false}
                  isClearable={true}
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "var(--mmp-surface)",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "var(--mmp-accent-bg)",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                      "&:hover": {
                        backgroundColor: "var(--mmp-accent-border)",
                        color: "var(--mmp-text-primary)",
                      },
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "var(--mmp-accent-bg)" : "var(--mmp-surface)",
                      color: "var(--mmp-text-primary)",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "var(--mmp-accent-bg)",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </div>

            {/* Filter by Home Prices */}
            <div className="col-md-6 col-lg-4">
              <Form.Group className="form-group">
                <Form.Label className="col-label">
                  Filter by Home Prices
                </Form.Label>
                <Select
                  isMulti
                  value={selectedHomePrices}
                  onChange={setSelectedHomePrices}
                  options={homePriceOptions}
                  placeholder="Select home price range..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPosition="fixed"
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "var(--mmp-surface)",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "var(--mmp-accent-bg)",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                      "&:hover": {
                        backgroundColor: "var(--mmp-accent-border)",
                        color: "var(--mmp-text-primary)",
                      },
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "var(--mmp-accent-bg)" : "var(--mmp-surface)",
                      color: "var(--mmp-text-primary)",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "var(--mmp-accent-bg)",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </div>

            {/* Filter by Mortgage */}
            <div className="col-md-6 col-lg-4">
              <Form.Group className="form-group">
                <Form.Label className="col-label">
                  Filter by Mortgage
                </Form.Label>
                <Select
                  isMulti
                  value={selectedMortgage}
                  onChange={setSelectedMortgage}
                  options={mortgageOptions}
                  placeholder="Select mortgage range..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPosition="fixed"
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "var(--mmp-surface)",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "var(--mmp-accent-bg)",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                      "&:hover": {
                        backgroundColor: "var(--mmp-accent-border)",
                        color: "var(--mmp-text-primary)",
                      },
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "var(--mmp-accent-bg)" : "var(--mmp-surface)",
                      color: "var(--mmp-text-primary)",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "var(--mmp-accent-bg)",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </div>

            {/* Filter by Rent */}
            <div className="col-md-6 col-lg-4">
              <Form.Group className="form-group">
                <Form.Label className="col-label">Filter by Rent</Form.Label>
                <Select
                  isMulti
                  value={selectedRent}
                  onChange={setSelectedRent}
                  options={rentOptions}
                  placeholder="Select rent range..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPosition="fixed"
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "var(--mmp-surface)",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "var(--mmp-accent-bg)",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                      "&:hover": {
                        backgroundColor: "var(--mmp-accent-border)",
                        color: "var(--mmp-text-primary)",
                      },
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "var(--mmp-accent-bg)" : "var(--mmp-surface)",
                      color: "var(--mmp-text-primary)",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "var(--mmp-accent-bg)",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </div>

            {/* Filter by Hospital Count */}
            <div className="col-md-6 col-lg-4">
              <Form.Group className="form-group">
                <Form.Label className="col-label">
                  Filter by Hospital Count
                </Form.Label>
                <Select
                  value={selectedHospital}
                  onChange={setSelectedHospital}
                  options={hospitalCountOptions}
                  placeholder="Select hospital count..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPosition="fixed"
                  isMulti={false}
                  isClearable={true}
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "var(--mmp-surface)",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "var(--mmp-accent-bg)",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                      "&:hover": {
                        backgroundColor: "var(--mmp-accent-border)",
                        color: "var(--mmp-text-primary)",
                      },
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "var(--mmp-accent-bg)" : "var(--mmp-surface)",
                      color: "var(--mmp-text-primary)",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "var(--mmp-accent-bg)",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </div>

            {/* Filter by Climate (summer temperature) */}
            <div className="col-md-6 col-lg-4">
              <Form.Group className="form-group">
                <Form.Label className="col-label">Filter by Climate</Form.Label>
                <Select
                  isMulti
                  value={selectedClimate}
                  onChange={setSelectedClimate}
                  options={climateOptions}
                  placeholder="Select summer temperature..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPosition="fixed"
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "var(--mmp-surface)",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "var(--mmp-accent-bg)",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "var(--mmp-text-primary)",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "var(--mmp-text-secondary)",
                      "&:hover": {
                        backgroundColor: "var(--mmp-accent-border)",
                        color: "var(--mmp-text-primary)",
                      },
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "var(--mmp-accent-bg)" : "var(--mmp-surface)",
                      color: "var(--mmp-text-primary)",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "var(--mmp-accent-bg)",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </div>
          </div>

          {/* Clear Filters Button */}
          <div className="d-flex justify-content-around justify-content-lg-end mt-2">
            {/* <button
              className="btn btn-secondary me-2"
              onClick={() => {
                setSelectedStates([]);
                setSelectedCities([]);
                setSelectedSalary([]);
                setSelectedRoles([]);
                setSelectedCrimeRate([]);
                setSelectedHomePrices([]);
                setSelectedMortgage([]);
                setSelectedRent([]);
              }}
            >
              Clear All Filters
            </button> */}
            <button
              className="btn btn-secondary me-2"
              onClick={handleClearFilters}
            >
              Clear All Filters
            </button>
            <button className="btn btn-primary" onClick={applyFilters}>
              Apply Filters
            </button>
          </div>
        </div>
      )}

      <div className="mmp-results-bar" data-tour="search-resultsbar">
        <p className="results-count" aria-live="polite">
          {loading && !showBookmarksOnly ? (
            <>
              <span className="results-count-skeleton" aria-hidden="true" />
              <span className="results-count-loading-text">
                Loading results…
              </span>
            </>
          ) : (
            <>
              <b className="tnum">
                {(showBookmarksOnly
                  ? bookmarkTotal
                  : perPageFromStore
                )?.toLocaleString("en-US")}
              </b>{" "}
              result
              {(showBookmarksOnly ? bookmarkTotal : perPageFromStore) === 1
                ? ""
                : "s"}
            </>
          )}
        </p>

        {!showBookmarksOnly && (
          <div className="sort-wrap" data-tour="search-sort">
            <Select
              value={selectedSort}
              onChange={(option) => {
                setSelectedSort(option);
                setSortBy(option.sort);
                setFiledName(option.field);

                const session_id = sessionStorage.getItem(
                  "comparison_session_id",
                );
                if (session_id && option?.track) {
                  dispatch(
                    ComparisonTrack({
                      event_type: "filter_applied",
                      event_data: {
                        session_id,
                        filters: Array.isArray(option.track)
                          ? option.track
                          : [option.track],
                      },
                    }),
                  );
                }
                const searchSessionId = sessionStorage.getItem("session_id");
                if (searchSessionId && option?.track) {
                  dispatch(
                    searchAnalytics({
                      session_id: searchSessionId,
                      sort_method: option.track,
                      results_count: perPageFromStore,
                    }),
                  );
                }
              }}
              options={sortbyData.map((s) => ({
                value: s.value,
                label: s.text,
                sort: s.sort,
                field: s.field,
                track: s.track,
              }))}
              classNamePrefix="mmp-sort-select"
              menuPosition="fixed"
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              isSearchable={false}
              aria-label="Sort by"
            />
          </div>
        )}

        <div
          className="view-toggle"
          role="group"
          aria-label="View mode"
          data-active={viewMode}
          data-tour="search-view-toggle"
        >
          <button
            type="button"
            className={viewMode === "list" ? "is-active" : ""}
            aria-label="List view"
            aria-pressed={viewMode === "list"}
            onClick={() => setViewMode("list")}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <button
            type="button"
            className={viewMode === "grid" ? "is-active" : ""}
            aria-label="Grid view"
            aria-pressed={viewMode === "grid"}
            onClick={() => setViewMode("grid")}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </button>
        </div>
      </div>

      {/* Quota / upgrade pill — sits between the results bar and the grid */}
      <div className="mmp-search-topbar quota-after-results">
        {(() => {
          // Gate on plan slug from profile so the pill renders immediately
          // for Free/Plus users — even before filter-city resolves the
          // usage payload. Once usage arrives we swap in the real counts.
          const profilePlanSlug = (
            profileStore?.profileItem?.data?.plan?.slug ||
            authStore?.user?.plan?.slug ||
            ""
          ).toLowerCase();
          const usagePlanSlug = filterCityUsage?.plan_slug;
          const effectivePlanSlug = usagePlanSlug || profilePlanSlug;

          if (
            !effectivePlanSlug ||
            effectivePlanSlug === "pro" ||
            effectivePlanSlug === "plus" ||
            effectivePlanSlug === "pro-trial" ||
            effectivePlanSlug === "pro-newsletter" ||
            effectivePlanSlug === "free-trial"
          ) {
            return null;
          }
          if (filterCityUsage?.comparisanMaxCount === -1) return null;

          const defaultMax = 1;
          const usedCount = Number(filterCityUsage?.used_count ?? 0);
          const maxCount = Number(
            filterCityUsage?.comparisanMaxCount ?? defaultMax,
          );
          const isCapReached =
            filterCityUsage?.is_comparisan === false ||
            (maxCount > 0 && usedCount >= maxCount);
          const planLabel = "free";

          const handleQuotaPillClick = () => {
            setUpgradeSource("city_detail_limit");
            setSubText({
              header: "Subscription Required!",
              desc:
                filterCityUsage?.comparisanMessage ||
                `You've used ${usedCount} of ${maxCount} free city detail views today.`,
              massage1: "Plus plan",
              pluspricem: "$6.99/month",
              pluspricey: "$69.99/year",
              desc2: "",
              promassage: "Pro plan",
              propricem: "$2.99/month",
              propricey: "$29.99/year",
            });
            setSubAlertCompModalVisible(true);
          };

          // Show a shimmer in place of "X of Y" until the real usage payload
          // lands — avoids a frame of misleading "0 of 1" on first paint.
          const usageReady = Boolean(filterCityUsage);
          return (
            <div className="quota-row">
              <button
                type="button"
                className={`quota-pill ${isCapReached ? "is-cap-reached" : ""}`}
                onClick={handleQuotaPillClick}
                disabled={!usageReady}
              >
                <span className="dot" aria-hidden="true" />
                {usageReady ? (
                  <span className="tnum">
                    {usedCount} of {maxCount}
                  </span>
                ) : (
                  <span className="quota-skeleton" aria-label="Loading usage" />
                )}
                <span className="quota-label">
                  {planLabel} city detail views used today
                </span>
                <span className="quota-cta">Upgrade</span>
              </button>
            </div>
          );
        })()}
      </div>

      {/* Results Grid */}
      {loading && cityResults.length === 0 ? (
        <div
          className={`results-grid row g-3 ${
            viewMode === "list" ? "list-view" : ""
          }`}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <SearchCardSkeleton key={i} viewMode={viewMode} />
          ))}
        </div>
      ) : (
        <div
          className={`results-grid row g-3 ${
            viewMode === "list" ? "list-view" : ""
          }`}
        >
          {(showBookmarksOnly ? bookmarkedResults : cityResults).length > 0 ? (
            (showBookmarksOnly ? bookmarkedResults : cityResults).map((res, idx) => {
              const remainingAfterMortgage = Number(res.after_mortgage || 0);
              const remainingAfterRent = Number(res.after_rent || 0);

              const mortgageCost = Number(res?.mortgage_monthly || 0);
              const netMonthly = Number(res?.net_monthly_salary || 1);

              const mortgagePct = Math.min(
                (mortgageCost / netMonthly) * 100,
                100,
              );

              const rentCost = Number(res?.rent_monthly || 0);

              const rentPct = Math.min((rentCost / netMonthly) * 100, 100);

              const keepMax = Math.max(
                1,
                ...(showBookmarksOnly
                  ? bookmarkedResults
                  : cityResults
                ).map((x) =>
                  Math.max(
                    Number(x.after_rent || 0),
                    Number(x.after_mortgage || 0),
                  ),
                ),
              );
              const rentBarColor =
                remainingAfterRent > 3000
                  ? "var(--mmp-success)"
                  : remainingAfterRent > 1500
                  ? "var(--mmp-warning)"
                  : "var(--mmp-danger)";
              const mortgageBarColor =
                remainingAfterMortgage > 2000
                  ? "var(--mmp-success)"
                  : remainingAfterMortgage > 500
                  ? "var(--mmp-warning)"
                  : "var(--mmp-danger)";
              const rentBarWidth = Math.max(
                0,
                Math.min(100, (remainingAfterRent / keepMax) * 100),
              );
              const mortgageBarWidth = Math.max(
                0,
                Math.min(100, (remainingAfterMortgage / keepMax) * 100),
              );
              const downPmt = Number(res.home_prices || 0) * 0.2;
              const crimeKey = (res.app_display || res.color_code || "")
                .toString()
                .toLowerCase();
              const crimePillStyle =
                CRIME_PILL_MAP[crimeKey] || CRIME_PILL_MAP.unavailable;
              const affordState = (pct) =>
                pct < 28 ? "good" : pct < 36 ? "warn" : "bad";

              return (
                <div
                  className={`result-card ${
                    viewMode === "list" ? "col-12" : "col-md-6 col-xl-4"
                  }`}
                  data-tour={idx === 0 ? "search-citycard" : undefined}
                  key={res.id}
                >
                  <div className="contentwrap rc-card">
                    {/* Navy header */}
                    <div className="rc-header">
                      <div className="rc-header-info">
                        <div className="rc-city">
                          {res.city}, {res.state}
                        </div>
                        {/* {res.role && (
                            <div className="rc-role">{res.role}</div>
                          )} */}
                      </div>
                      <button
                        type="button"
                        className="rc-fav"
                        data-tour={idx === 0 ? "search-cc-fav" : undefined}
                        aria-label="Bookmark city"
                        onClick={(e) => handleBookmarkToggle(e, res)}
                      >
                        <FiHeart
                          className={
                            showBookmarksOnly || bookmarkedCities.has(res.id)
                              ? "bookmarked"
                              : ""
                          }
                        />
                      </button>
                    </div>

                    <div className="rc-body">
                      {/* Crime + tax pills */}
                      <div className="rc-pills">
                        <span
                          className="rc-pill rc-pill-crime"
                          style={crimePillStyle}
                        >
                          {res.app_display || "Unavailable"}
                        </span>
                        {res.compact_state?.toLowerCase() === "yes" && (
                          <span className="rc-pill rc-pill-compact">
                            <CheckIcon /> Compact
                          </span>
                        )}
                        <span
                          className="rc-pill rc-pill-tax"
                          style={{
                            ...getTaxStyle(res?.income_tax_percentage),
                            color: "var(--mmp-text-primary)",
                            background: "var(--mmp-hover-bg)",
                            border: "none",
                          }}
                        >
                          Tax{" "}
                          {formatPercent(res?.income_tax_percentage).toFixed(0)}
                          %
                        </span>
                      </div>

                      {/* Salary + housing columns */}
                      <div className="rc-cols">
                        <div className="rc-col">
                          <div className="rc-col-label">Gross Avg Salary</div>
                          <div className="rc-col-value">
                            $
                            {Math.floor(
                              Number(res.median_annual_salary || 0),
                            ).toLocaleString("en-US")}
                            <span className="rc-col-unit">/yr</span>
                          </div>
                          <div className="rc-col-sub">
                            $
                            {Number(
                              res.gross_hourly_salary || 0,
                            ).toLocaleString("en-US")}
                            /hr · $
                            {Math.floor(
                              Number(res.net_monthly_salary || 0),
                            ).toLocaleString("en-US")}
                            /mo net
                          </div>
                        </div>
                        <div className="rc-col">
                          <div className="rc-col-label">Home Price</div>
                          <div className="rc-col-value">
                            {res.home_prices
                              ? `$${
                                  Number(res.home_prices) < 0 ? "-" : ""
                                }${formatMoney(res.home_prices)}`
                              : "—"}
                          </div>
                          <div className="rc-col-sub">
                            $
                            {Number(
                              Math.abs(
                                Number(res.mortgage_monthly || 0),
                              ).toFixed(0),
                            ).toLocaleString("en-US")}
                            /mo own · $
                            {Number(
                              Math.abs(
                                Number(res.average_monthly_rent_prices || 0),
                              ).toFixed(0),
                            ).toLocaleString("en-US")}
                            /mo rent
                          </div>
                        </div>
                      </div>

                      {/* What you keep after housing */}
                      <div
                        className="rc-keep"
                        data-tour={idx === 0 ? "search-cc-keep" : undefined}
                      >
                        <div className="rc-keep-label">
                          What you keep after housing
                        </div>
                        <div className="rc-bar">
                          <div className="rc-bar-head">
                            <span>After Rent</span>
                            <span
                              className={
                                remainingAfterRent >= 0
                                  ? "positive"
                                  : "negative"
                              }
                            >
                              {remainingAfterRent < 0 ? "-$" : "$"}
                              {Number(
                                Math.abs(remainingAfterRent).toFixed(0),
                              ).toLocaleString("en-US")}
                            </span>
                          </div>
                          <div className="rc-bar-track">
                            <div
                              className="rc-bar-fill"
                              style={{
                                width: `${rentBarWidth}%`,
                                background: rentBarColor,
                              }}
                            />
                          </div>
                        </div>
                        <div className="rc-bar">
                          <div className="rc-bar-head">
                            <span>After Mortgage</span>
                            <span
                              className={
                                remainingAfterMortgage >= 0
                                  ? "positive"
                                  : "negative"
                              }
                            >
                              {remainingAfterMortgage < 0 ? "-$" : "$"}
                              {Number(
                                Math.abs(remainingAfterMortgage).toFixed(0),
                              ).toLocaleString("en-US")}
                            </span>
                          </div>
                          <div className="rc-bar-track">
                            <div
                              className="rc-bar-fill"
                              style={{
                                width: `${mortgageBarWidth}%`,
                                background: mortgageBarColor,
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Affordability row */}
                      <div className="rc-afford">
                        <span
                          className="rc-aff-pill"
                          data-state={affordState(rentPct)}
                        >
                          Rent <b>{rentPct.toFixed(0)}%</b>
                        </span>
                        <span
                          className="rc-aff-pill"
                          data-state={affordState(mortgagePct)}
                        >
                          Own <b>{mortgagePct.toFixed(0)}%</b>
                        </span>
                        <span className="rc-downpmt">
                          Down pmt <b>${formatMoney(downPmt) || "0"}</b>
                        </span>
                      </div>

                      {/* Facts row */}
                      <div className="rc-facts">
                        <span className="rc-fact">
                          <HospitalsIcon className="rc-fact-icon" />
                          {res.hospital_count ?? "—"}
                        </span>
                        <span className="rc-fact">
                          <HighTempIcon className="rc-fact-icon" />
                          {res.summer_high}°
                        </span>
                        <span className="rc-fact">
                          <LowTempIcon className="rc-fact-icon" />
                          {res.winter_low}°
                        </span>
                        <span className="rc-fact">
                          <PopulationIcon className="rc-fact-icon" />
                          {res.total_population > 0
                            ? formatNumber(res.total_population)
                            : "N/A"}
                        </span>
                      </div>

                      {/* CTAs */}
                      <div className="rc-actions">
                        <button
                          className="rc-btn rc-btn-compare"
                          data-tour={idx === 0 ? "search-cc-compare" : undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCompareClick(res);
                          }}
                        >
                          {compareCities.some((c) => c.id === res.id)
                            ? "Added ✓"
                            : "Compare"}
                        </button>
                        <button
                          className="rc-btn rc-btn-details"
                          data-tour={idx === 0 ? "search-cc-view" : undefined}
                          onClick={() => handleSearchClick(res)}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="no-data">
              {showBookmarksOnly
                ? "No bookmarked cities yet. Click the heart icon to bookmark cities!"
                : "No cities found"}
            </p>
          )}
        </div>
      )}
      {/* {compareCities.length > 0 && (
        <div className="compare-bar">
          <div className="compare-header">
            <span>QUICK COMPARE ({compareCities.length}/3)</span>
            <span className="clear" onClick={() => setCompareCities([])}>
              Clear all
            </span>
          </div>

          <div className="compare-content">
            {[
              ...compareCities,
              ...Array(MAX_COMPARE - compareCities.length).fill(null),
            ].map((city, i) =>
              city ? (
                <div className="compare-card" key={city.id}>
                  <div className="content-wrap">
                    <div
                      className="remove"
                      onClick={() => removeCompareCity(city.id)}
                    >
                      <RemoveIcon />
                    </div>

                    <h4>
                      {city.city}, {city.state}
                    </h4>

                    <div className="compare-grid">
                      <div>
                        <span>Housing Rent</span>
                        <strong>
                          {city.average_monthly_rent_prices
                            ? `${
                                city.average_monthly_rent_prices < 0 ? "-" : ""
                              }${
                                Math.abs(
                                  Number(city.average_monthly_rent_prices),
                                ).toFixed(0)
                                  ? Number(
                                      Math.abs(
                                        Number(
                                          city.average_monthly_rent_prices,
                                        ),
                                      ).toFixed(0),
                                    ).toLocaleString("en-US")
                                  : "0"
                              }/mo`
                            : "0/mo"}
                        </strong>
                      </div>

                      <div>
                        <span>Home Price</span>
                        <strong>
                          ${Number(city.home_prices || 0).toLocaleString()}
                        </strong>
                      </div>

                      <div>
                        <span>Mortgage</span>
                        <strong className="negative">
                          {city?.mortgage_monthly
                            ? `${city.mortgage_monthly < 0 ? "-" : ""}${Number(
                                Math.abs(city.mortgage_monthly).toFixed(0),
                              ).toLocaleString("en-US")}`
                            : "$0"}
                          /mo
                        </strong>
                      </div>

                      <div>
                        <span>Leftover Income</span>
                        <strong className="positive">
                          $
                          {Number(
                            city.true_net_monthly_salary_if_renting || 0,
                          ).toLocaleString()}
                          /mo
                        </strong>
                      </div>
                    </div>

                    <div
                      className="tag-badge"
                      style={{
                        backgroundColor:
                          city.color_code?.toLowerCase() === "yellow"
                            ? "#f09d08"
                            : city.color_code?.toLowerCase() || "#6b7280",
                        color: "#fff",
                      }}
                    >
                      {city.app_display}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="compare-empty" key={i}>
                  <div className="content-wrap">+ Add a city to compare</div>
                </div>
              ),
            )}
          </div>

          <div className="compare-footer">
            <button
              className="btn btn-primary"
              onClick={() => navigate("/dashboard")}
            >
              Open Full Comparison <ChevronRightIcon className="button-icon" />
            </button>
          </div>
        </div>
      )} */}
      {compareCities.length > 0 && (
        <div className="compare-bar">
          <div className="compare-header">
            <span>QUICK COMPARE ({compareCities.length}/3)</span>
            <span className="clear" onClick={() => setCompareCities([])}>
              Clear all
            </span>
          </div>

          <div className="compare-content">
            {[
              ...compareCities,
              ...Array(MAX_COMPARE - compareCities.length).fill(null),
            ].map((city, i) => {
              if (!city) {
                return (
                  <div className="compare-empty" key={i}>
                    {/* <div className="content-wrap">+ Add a city to compare</div> */}
                    <div className="content-wrap">
                      Click on compare button to add city
                    </div>
                  </div>
                );
              }

              // ✅ CALCULATIONS PER CITY
              const monthlySalary = Math.floor(
                Number(city.median_annual_salary || 0) / 12,
              );

              const remainingAfterMortgage = Number(city.after_mortgage || 0);

              const remainingPercent =
                monthlySalary > 0
                  ? (remainingAfterMortgage / monthlySalary) * 100
                  : 0;

              const safePercent = Math.max(0, Math.min(100, remainingPercent));

              const remainingAfterRent = Number(city.after_rent || 0);

              const rentPercent =
                monthlySalary > 0
                  ? (remainingAfterRent / monthlySalary) * 100
                  : 0;

              const safeRentPercent = Math.max(0, Math.min(100, rentPercent));

              const leftoverAfterAll = Number(
                city.total_leftover_after_rent_and_mortgage || 0,
              );

              const leftoverPercent =
                monthlySalary > 0
                  ? (leftoverAfterAll / monthlySalary) * 100
                  : 0;

              const safeLeftoverPercent = Math.max(
                0,
                Math.min(100, leftoverPercent),
              );

              return (
                <div className="compare-card" key={city.id}>
                  <div className="content-wrap">
                    <div
                      className="remove"
                      onClick={() => removeCompareCity(city.id)}
                    >
                      <RemoveIcon />
                    </div>

                    <h4>
                      {city.city}, {city.state}
                    </h4>

                    <div className="compare-grid">
                      {/* Rent */}
                      <div>
                        <span>Housing Rent</span>
                        <strong>
                          $
                          {Number(
                            city.average_monthly_rent_prices || 0,
                          ).toLocaleString()}
                          /mo
                        </strong>
                      </div>

                      {/* Home Price */}
                      <div>
                        <span>Home Price</span>
                        <strong>
                          {/* ${Number(city.home_prices || 0).toLocaleString()} */}
                          {city.home_prices && (
                            <>
                              ${Number(city.home_prices) < 0 ? "-" : ""}
                              {formatMoney(city.home_prices)}
                            </>
                          )}
                        </strong>
                      </div>

                      {/* ✅ After Mortgage (NEW) */}
                      <div>
                        <span>After Mortgage</span>
                        <strong
                          className={
                            remainingAfterMortgage >= 0
                              ? "positive"
                              : "negative"
                          }
                        >
                          {remainingAfterMortgage < 0 ? "-$" : "$"}
                          {Math.abs(remainingAfterMortgage).toLocaleString()}/mo
                        </strong>
                      </div>

                      {/* Leftover */}
                      <div>
                        <span>After Rent</span>
                        <strong
                          className={
                            remainingAfterRent >= 0 ? "positive" : "negative"
                          }
                        >
                          {remainingAfterRent < 0 ? "-$" : "$"}
                          {Number(
                            Math.abs(remainingAfterRent).toFixed(0),
                          ).toLocaleString("en-US")}
                          /mo
                        </strong>
                      </div>
                    </div>

                    {city.app_display && (
                      <div
                        className="tag-badge"
                        style={{
                          backgroundColor:
                            city.color_code?.toLowerCase() === "yellow"
                              ? "#f09d08"
                              : city.color_code?.toLowerCase() || "#6b7280",
                          color: "#fff",
                        }}
                      >
                        {city.app_display}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* <div className="compare-footer">
            <button
              className="btn btn-primary"
              onClick={() => navigate("/dashboard")}
            >
              Open Full Comparison <ChevronRightIcon className="button-icon" />
            </button>
          </div> */}
        </div>
      )}
      {showBookmarksOnly
        ? bookmarkedResults.length > 0 &&
          bookmarkedResults.length < bookmarkTotal && (
            <div className="load-more">
              <button onClick={handleLoadMore} className="btn btn-primary">
                {loadMoreLoading ? (
                  <>
                    <Spinner size="sm" /> Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </button>
            </div>
          )
        : cityResults.length > 0 &&
          cityResults.length < perPageFromStore && (
            <div className="load-more">
              <button onClick={handleLoadMore} className="btn btn-primary">
                {/* Load More */}
                {loadMoreLoading ? (
                  <>
                    <Spinner size="sm" /> Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </button>
            </div>
          )}
      <ComparisonModal
        title={subText?.header}
        description={subText.desc}
        massage1={subText.massage1}
        pluspricem={subText.pluspricem}
        pluspricey={subText.pluspricey}
        prodesc={subText.desc2}
        promassage={subText.promassage}
        propricem={subText.propricem}
        propricey={subText.propricey}
        plan={subText.plan}
        lockedItemName={subText.lockedItemName}
        isVisible={subAlertCompModalVisible}
        eventSource={upgradeSource}
        onClose={() => {
          setSubAlertCompModalVisible(false);
        }}
        onPressYes={() => {
          // Navigate to subscription page or handle upgrade
          navigate("/profile/subscription");
          setSubAlertCompModalVisible(false);
        }}
        yesText="Upgrade Now"
        noText="Cancel"
        // extraData={isRestrictedData}
        extraMetaInfo={true}
      />
      <ActionModal
        title={subText.header}
        description={subText.desc}
        isVisible={subAlertModalVisible}
        onClose={() => setSubAlertModalVisible(false)}
        eventSource={upgradeSource}
        onPressYes={async () => {
          setSubAlertModalVisible(false);
          navigate("Subscription");
        }}
        yesText={`Upgrade Now`}
        noText="Cancel"
        extraMetaInfo={true}
        extraData={isRestrictedData}
        userData={userPlanData}
      />
      <CityDetailLimitModal
        isVisible={cityLimitModalVisible}
        onClose={() => setCityLimitModalVisible(false)}
        onUpgrade={recordSearchUpgrade}
        eventSource={upgradeSource}
        heading={cityLimitModalData.heading}
        infoText={cityLimitModalData.infoText}
      />
      <CityDetailLimitModal
        isVisible={citySearchLimitVisible}
        onClose={() => setCitySearchLimitVisible(false)}
        onUpgrade={recordSearchUpgrade}
        eventSource="city_search_limit"
        heading="You've used your free city search today"
        infoText="Pro users get unlimited city searches"
      />
      <RestrictionModal
        isVisible={loadMoreLimitVisible}
        onClose={() => setLoadMoreLimitVisible(false)}
        eventSource={upgradeSource}
        iconType="search-pin"
        heading="Want to view more city details?"
        subText="Upgrade to Pro to explore detailed city information without limits."
        infoText="Pro users can view all cities and access detailed information without limits."
      />

      <RestrictionModal
        isVisible={showSaveCityModal}
        onClose={() => setShowSaveCityModal(false)}
        eventSource="save_city"
        iconType="search-pin"
        thirdCtaMode="newsletter"
        heading="Want to save more cities?"
        subText="Free users can save 1 city per day. Upgrade to Pro to save unlimited cities and access them anytime in Your Cities."
        infoText="Track and compare as many cities as you want with unlimited saved cities on Pro."
      />

      <GuidedTour
        isOpen={isTourOpen}
        steps={searchTourSteps}
        onDone={finishTour}
      />
    </div>
  );
};

export default Search;
